import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import {
  alpha,
  Alert,
  Box,
  ButtonBase,
  Card,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  InputAdornment,
  InputBase,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { radius } from '@karehero/llama/theme/radius';

import StatusBar from '@/designs-dev/device/StatusBar';
import HomeIndicator from '@/designs-dev/device/HomeIndicator';
import MobileKeyboard, {
  KEYBOARD_H,
} from '@/designs-dev/device/MobileKeyboard';
import BottomSheet from '@/designs-dev/device/BottomSheet';
import ModalScreen from '@/designs-dev/device/ModalScreen';
import KeniOrb from '@/components/wrapped/KeniOrb';
import OptionCard from '@/components/wrapped/OptionCard';
import ChatActionRow from '@/components/wrapped/ChatActionRow';
import ChatTyping from '@/components/wrapped/ChatTyping';
import ChatBubble from '@/karehero-app/tell-us-once/intake/ChatBubble';
import QuickReplies from '@/karehero-app/tell-us-once/intake/QuickReplies';
import ReadingBubble from '@/karehero-app/tell-us-once/intake/ReadingBubble';
import ChatMenuMessage from '@/karehero-app/tell-us-once/intake/ChatMenuMessage';
import LiveCaptionCard from '@/karehero-app/tell-us-once/intake/LiveCaptionCard';
import FromUsCard from '@/karehero-app/tell-us-once/intake/FromUsCard';
import Attachment, {
  type AttachmentKind,
  type AttachmentState,
} from '@/karehero-app/tell-us-once/intake/Attachment';
import FailedMessage from '@/karehero-app/tell-us-once/intake/FailedMessage';
import VoiceNote from '@/karehero-app/tell-us-once/intake/VoiceNote';
import ClaimRow from '@/karehero-app/tell-us-once/intake/ClaimRow';
import FactList from '@/karehero-app/tell-us-once/intake/FactList';
import FactsSheet from '@/karehero-app/tell-us-once/intake/FactsSheet';
import FloatingAsk, {
  type AskTone,
} from '@/karehero-app/tell-us-once/intake/FloatingAsk';
import ScreenRoot from '@/components/wrapped/ScreenRoot';
import AccountBar, { type Account } from '@/karehero-app/access/AccountBar';
import {
  INTAKE_HOW_TO,
  type ChatQuestion,
  type FromUs,
  type FailureKind,
  INTAKE_ATTACH,
  INTAKE_FAILURES,
  INTAKE_UPLOADS,
} from '@/karehero-app/tell-us-once/intake/content';

// Option 2's chat: the opening questions asked in the conversation rather than
// on screens of their own.
//
// A closed question puts its answers in the transcript, under the bubble that
// asked, and confirms with Next. Nothing is listening and there is nothing to
// type, so neither the orb line nor the input is shown. An open question does
// the opposite: talk or type, and the orb only says it is listening once tapped.
//
// Deliberately a copy of ChatV1 rather than a shared component with a flag. The
// two options are being compared, so either has to be free to change without
// moving the other.
// How long one message takes to read, where each is read as it arrives.
export const READ_EACH_MS = 3000;

// What an answer costs before the next question arrives: the dots. Long enough
// to read as somebody reading, short enough that nobody waits for it.
export const THINK_MS = 900;

type Msg = {
  from: 'keni' | 'user';
  text: string;
  seconds?: number;
  /** The way on, sitting in the conversation where it was offered. */
  aside?: NonNullable<ChatQuestion['asides']>[number];
  /** Something they handed over did not work out. Sits where it happened. */
  failure?: FailureKind;
  /** This message did not send. Carried by the message, not an alert under it. */
  failed?: boolean;
  /** They handed a file over rather than typing or speaking. */
  attachment?: AttachmentKind;
  /** Whether that file has finished leaving the phone. */
  attachmentState?: AttachmentState;
  /**
   * Which question this reply answered, where answers are kept. Tapping the
   * reply opens that question's rows in a sheet to change it.
   */
  qi?: number;
  /** Says an earlier answer changed. One per question, replaced on a change. */
  notedFor?: number;
  /** A card from us, given back before the question it is attached to. */
  card?: FromUs;
  /** Claims handed back, as the calculator's result rows. */
  claims?: ChatQuestion['claims'];
  /** What we hold, as answer rows that edit in a drawer. */
  facts?: ChatQuestion['facts'];
  /** The ask of a question whose rows are attached under it, by its id. */
  menuFor?: string;
  /** Being read. Set while it is; a spinner sits beside it. */
  reading?: number;
  /** A voice note's words, as the live captions heard them. */
  transcript?: string;
  /** A correction to a card, sent from the composer. Not one of the things asked for. */
  edit?: boolean;
};

/**
 * A message still being read: muted, with a spinner on its left. A file still leaving
 * the phone already shows that, so it waits until it has arrived.
 */
const withReading = (m: Msg, i: number, node: ReactNode, readingNow = false) =>
  (m.reading || readingNow) && m.attachmentState !== 'uploading' ? (
    <Stack
      key={i}
      sx={{
        alignSelf: 'flex-end',
        // The reply's own width cap, held here instead: a bubble capping
        // itself at a share of this shrink-to-fit row wraps early and leaves
        // a gap on its right.
        maxWidth: '82%',
        // A voice note with its words open is tall, so the spinner sits
        // beside the audio at the top rather than halfway down the words.
        alignItems: m.transcript ? 'flex-start' : 'center',
      }}
      direction='row'
      spacing={1}
    >
      <CircularProgress
        aria-label='Reading this'
        size={14}
        thickness={5}
        sx={{
          color: 'custom.neutral.600',
          flexShrink: 0,
          // Half of the audio row's height, less half the spinner's.
          mt: m.transcript ? '15px' : 0,
        }}
      />
      {/* Muted until it has been read. Fills the row, so the reply takes the
          width it would have outside it. */}
      <Box sx={{ opacity: 0.6, minWidth: 0, '& > *': { maxWidth: '100%' } }}>
        {node}
      </Box>
    </Stack>
  ) : (
    node
  );

/** A row that is one of the question's answers, not an offer or the way on. */
const isAnswerRow = (aside: NonNullable<ChatQuestion['asides']>[number]) =>
  !aside.tone && !aside.uploads && !aside.opensInput;

// Every row a question offers, in the order it offers them, as messages: the
// offers first, then the way on.
// What the app says when a question is asked. A second bubble rather than a
// line in the bar: the bar is for controls, and a thing said once belongs in the
// conversation like everything else said once.
/** Where answering question `i` with `said` goes. See ChatQuestion.next. */
const nextFrom = (
  questions: ChatQuestion[],
  i: number,
  said?: string,
): number => {
  const next = questions[i]?.next;
  const to = (said !== undefined ? next?.[said] : undefined) ?? next?.['*'];
  if (!to) return i + 1;
  if (to === 'end') return questions.length;
  const at = questions.findIndex((q) => q.id === to);
  return at < 0 ? i + 1 : at;
};

/**
 * The questions a scene opened at `startAt` passed through, in order, by each
 * one's harness answer. A straight run where nothing branches; falls back to
 * one when the path never reaches it.
 */
const pathTo = (questions: ChatQuestion[], startAt: number): number[] => {
  const path: number[] = [];
  let i = 0;
  while (i < questions.length && path.length <= questions.length) {
    path.push(i);
    if (i === startAt) return path;
    const q = questions[i];
    i = nextFrom(questions, i, q.answer ?? q.options?.[0] ?? 'Yes');
  }
  return Array.from({ length: startAt + 1 }, (_, k) => k);
};

const askMsgs = (question?: ChatQuestion): Msg[] =>
  question
    ? [
        // What we already hold, before the question about it.
        ...(question.says ?? []).map((text) => ({
          from: 'keni' as const,
          text,
        })),
        // Then anything given back, last before the ask it leads into.
        ...(question.card && !question.card.after
          ? [{ from: 'keni' as const, text: '', card: question.card }]
          : []),
        ...(question.thenCard
          ? [{ from: 'keni' as const, text: '', card: question.thenCard }]
          : []),
        // What we hold, as rows, before the ask about what is missing.
        ...(question.facts
          ? [{ from: 'keni' as const, text: '', facts: question.facts }]
          : []),
        // A question can be asked by its card instead, with no bubble.
        ...(question.ask
          ? [
              {
                from: 'keni' as const,
                text: question.ask,
                ...(question.attachRows ? { menuFor: question.id } : {}),
              },
            ]
          : []),
        ...(question.hint
          ? [{ from: 'keni' as const, text: question.hint }]
          : []),
        // Claims under the ask that hands them back.
        ...(question.claims
          ? [{ from: 'keni' as const, text: '', claims: question.claims }]
          : []),
        // Prompts sit under the question they help answer.
        ...(question.card?.after
          ? [{ from: 'keni' as const, text: '', card: question.card }]
          : []),
      ]
    : [];

const asideMsgs = (question?: ChatQuestion, answered = false): Msg[] =>
  // Attached to the ask instead, so not rows of their own.
  (question?.attachRows ? [] : (question?.asides ?? []))
    .filter((aside) => answered || aside.after !== 'answer')
    .map((aside) => ({ from: 'keni' as const, text: '', aside }));

/**
 * New messages land above the way on, so it stays the last thing in the
 * conversation however much gets sent. An offer is not the way on: it stays
 * where it was made, under the question, and the notes pile up below it.
 */
/** The rows this question was holding back, once and once only. */
const heldBack = (prev: Msg[], question: ChatQuestion): Msg[] =>
  prev.some((m) => m.aside?.after === 'answer')
    ? []
    : (question.asides ?? [])
        .filter((aside) => aside.after === 'answer')
        .map((aside) => ({ from: 'keni' as const, text: '', aside }));

const above = (prev: Msg[], added: Msg[]): Msg[] => {
  let cut = prev.length;
  while (cut > 0 && prev[cut - 1].aside?.tone === 'go') cut -= 1;
  return [...prev.slice(0, cut), ...added, ...prev.slice(cut)];
};

// Maps a question's aside onto the shared row. The styling moved to
// ChatActionRow, so the pre-sign-up chat and this one cannot drift apart.
const AsideRow = ({
  aside,
  selectable = false,
  selected = false,
  onClick,
}: {
  aside: NonNullable<ChatQuestion['asides']>[number];
  /** One of several answers rather than a single offer. See ChatActionRow. */
  selectable?: boolean;
  selected?: boolean;
  onClick: () => void;
}) => (
  <ChatActionRow
    label={aside.label}
    icon={aside.icon}
    tone={aside.tone === 'go' ? 'go' : 'offer'}
    selectable={selectable}
    selected={selected}
    onClick={onClick}
  />
);

/**
 * Whether a row is one of several answers rather than a one-tap offer.
 *
 * Only on a question that stays open, and only for the rows that are answers:
 * the way on closes the question rather than adding to it, and an upload hands
 * a file over rather than ticking a box.
 */
const isChoice = (
  question: ChatQuestion | undefined,
  aside: NonNullable<ChatQuestion['asides']>[number],
) => Boolean(question?.multi) && !aside.tone && !aside.uploads;

// A failure in the conversation rather than instead of it: the transcript stays
// and the failure sits where it happened.
//
// The message only. Trying again is what the composer below is for, in whichever
// way they want this time, so a button on the alert would be a second door to the
// same room.
//
// Stock MUI Alert. The severity colours are MUI's own, since llama overrides no
// semantic palette, so this is redder than the brand coral a hand-rolled row
// would reach for. That is the point: an alert is an alert, and this flow should
// not carry a private one.
const FailureRow = ({ kind }: { kind: FailureKind }) => (
  <Alert severity='error'>{INTAKE_FAILURES[kind].text}</Alert>
);

// The round coral action in the bar: send a typed note, send a recording, stop
// recording. One definition because it is one button — three copies of this had
// drifted to two different corals and two different sizes, so the draft on the
// voice screen did not match the composer's send.
/**
 * Which bar a question gets. A closed question gets one control; an open one
 * gets the input, with the orb above it unless the answer has to be typed.
 *
 * Three kinds rather than two, because the orb arriving is as much a change of
 * bar as the input arriving. Two typed questions in a row are the same bar with
 * a new placeholder, and animating that would say something changed when
 * nothing did.
 */
/** Whether this question holds its composer back behind an offer. */
const locks = (question?: ChatQuestion) =>
  (question?.asides ?? []).some((aside) => aside.opensInput);

const footerKind = (question?: ChatQuestion, unlocked = false) => {
  if (question?.mode === 'rows') return 'none';
  // Held back until the row that opens it is tapped, so the question arrives
  // as a choice between its offers rather than as a text field.
  if (locks(question) && !unlocked) return 'none';
  if (question && question.mode !== 'say') return 'closed';
  return question?.voice === false ? 'typed' : 'spoken';
};

/**
 * How the orb arrives when a typed question is followed by a spoken one.
 *
 * 'grow': the input stays where it is, the bar grows to make room and the orb
 * scales up into it. Both bars are the same composer, so dropping one to bring
 * the other back read as the input going away and coming back.
 *
 * 'drop': the previous behaviour, where any change of kind drops the old bar
 * and brings the new one up. Set this to go back to it.
 */
const ORB_ARRIVAL: 'grow' | 'drop' = 'grow';

const isComposer = (kind: ReturnType<typeof footerKind>) =>
  kind === 'typed' || kind === 'spoken';

// The staged photo's arrival, on the composer's own curve so the two read as one
// movement.
export const STAGE_MS = 280;
const STAGE_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

/**
 * The calculator's tap size: Apple's 44pt rather than 40, for older carers and
 * less steady hands. Onboarding's composer stays at 40 until its next version.
 */
const TAP = 44;

/**
 * A quiet round button: the composer's plus. The calculator bar's back chevron
 * is the same button, white with an edge, since grey disappears on Gardenia.
 */
export const pillButton = {
  width: TAP,
  height: TAP,
  flexShrink: 0,
  color: 'text.primary',
  bgcolor: 'custom.neutral.100',
  '&:hover': { bgcolor: 'custom.neutral.200' },
} as const;

const sendButton = {
  width: 40,
  height: 40,
  flexShrink: 0,
  bgcolor: 'custom.coral.600',
  color: 'common.white',
  '&:hover': { bgcolor: 'custom.coral.700' },
  '&.Mui-disabled': {
    bgcolor: 'custom.neutral.200',
    color: 'common.white',
  },
} as const;

/** The coral round button at the calculator's tap size. */
const tapButton = { ...sendButton, width: TAP, height: TAP } as const;

/**
 * Keni in a Marine Green circle. Keni is pale, and on anything lighter the
 * ears disappear. The drawing is scaled to the circle: big enough to read as a
 * llama, the ears just inside the top and the body cropped by the bottom.
 */
const KeniAvatar = ({ size }: { size: number }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: '50%',
      overflow: 'hidden',
      bgcolor: 'primary.main',
      display: 'flex',
      justifyContent: 'center',
    }}
  >
    <Box
      component='img'
      src='/vector/keni.svg'
      alt=''
      sx={{
        width: Math.round(size * 0.9),
        mt: `${Math.round(size * 0.05)}px`,
        alignSelf: 'flex-start',
      }}
    />
  </Box>
);

/** How long they talk before a pause sends it, live. */
export const LIVE_TURN_MS = 3200;

/** Bars in the live bar's soundwave, and how tall each one peaks. */
/** How long the mic takes to grow into the wave, and back. */
const LIVE_GROW_MS = 360;

/** Their latest words, on the composer's one line while they talk. */
const LIVE_LINE_WORDS = 7;

const LIVE_BARS = 44;
/**
 * Each bar's peak: tallest in the middle, tapering to the ends, with a little
 * unevenness so it reads as a voice rather than a sine wave.
 */
const LIVE_PEAKS = Array.from({ length: LIVE_BARS }, (_, i) => {
  const middle = Math.sin((Math.PI * (i + 0.5)) / LIVE_BARS);
  return 0.25 + 0.75 * middle * [1, 0.75, 0.9, 0.65, 0.95, 0.8][i % 6];
});

/** How long stopping a recording takes to settle into the send row. */
export const REC_MS = 280;
const REC_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

/**
 * How long a card that pins itself again takes to come back: the leaving in
 * reverse, sliding down from the top of the chat flat, then lifting as its
 * shadow returns.
 */
export const PIN_RETURN_MS = 560;
/** Where in the return it has landed and starts to lift. */
const PIN_RETURN_LANDED = 0.6;
/**
 * And how long it waits first. Starting to record changes the bar, so the card
 * lands after the bar has settled rather than on top of it moving.
 */
export const PIN_RETURN_DELAY_MS = 140;

/**
 * Whether a sticky element is stuck: moved down from where it would sit in the
 * flow, just under the thing before it. Not measured against the scroller's
 * edge, since where a sticky element stops depends on the scroller's padding.
 * Scaled, because a mini is drawn smaller than it is laid out.
 */
const isStuck = (el: HTMLElement) => {
  const before = el.previousElementSibling;
  if (!before) return false;
  const scale = el.getBoundingClientRect().height / el.offsetHeight || 1;
  const gap = parseFloat(getComputedStyle(el).marginTop) * scale;
  const natural = before.getBoundingClientRect().bottom + gap;
  return el.getBoundingClientRect().top > natural + 2;
};

/**
 * How long a card that lets go takes to leave: pressing down as its shadow
 * goes, then sliding up out of the top of the chat.
 */
export const PIN_LEAVE_MS = 560;

/**
 * A trial: while the card is pinned and they are recording or typing, a gentle
 * scrim over the rest of the chat, so the card and the bar are what is left.
 * Drawn as a spread shadow on the card's own holder, so the chat's scroll box
 * clips it to the transcript and the bar stays clear.
 */
const PIN_SCRIM = 0.16;
export const PIN_SCRIM_MS = 280;

/** How long recording takes to tick each item, for the demo. */
export const HEARD_MS = 2500;

/**
 * A card whose items are ticked off as they are handed over, held at the top
 * of the chat until the question that gathers them is answered.
 *
 * Ticked by counting what they have sent since that question was asked: each
 * message, voice note or file ticks the next item. In the app each tick is
 * the reading finding that thing; here it stands in for it.
 *
 * Held with `position: sticky` inside the transcript, so it scrolls up with
 * the conversation until it reaches the top and then stays there while they
 * work through it. It lets go once they say they are done.
 */
const ChecklistCard = ({
  card,
  questions,
  index,
  transcript,
  reading,
  recording,
  composing,
  missed = 0,
  onDim,
}: {
  card: FromUs;
  questions: ChatQuestion[];
  index: number;
  transcript: Msg[];
  /** What they said is being read now. */
  reading: boolean;
  /** They are recording the answer now. */
  recording: boolean;
  /** They are recording or typing something. */
  composing: boolean;
  /** Harness-only: how many of the last items the reading does not find. */
  missed?: number;
  /** Told when the scrim is up, so the chat can dim the bars above it too. */
  onDim?: (dim: boolean) => void;
}) => {
  const total = card.items?.length ?? 0;
  const findable = total - missed;
  const until = questions.findIndex((q) => q.id === card.checklistUntil);
  const asked = transcript.findIndex(
    (m) => m.from === 'keni' && m.text === questions[until]?.ask,
  );
  const sent =
    asked < 0
      ? 0
      : transcript.slice(asked + 1).filter(
          (m) =>
            m.from === 'user' &&
            !m.aside &&
            !m.reading &&
            !m.edit &&
            // The way on is not one of the things asked for.
            !questions[until]?.quickReplies?.some((r) => r.label === m.text),
        ).length;
  // Before they have agreed to start, nothing is next yet.
  const counted = asked < 0 ? -1 : Math.min(sent, total);
  // Ticked by the reading instead: one after another across the time the
  // reading takes, ending with every item ticked a beat before it lands.
  const [found, setFound] = useState(0);
  const readMs = questions[until]?.reading?.ms ?? 3000;
  useEffect(() => {
    if (!card.checklistReads || !reading) return;
    setFound(0);
    const id = window.setInterval(
      () => setFound((n) => Math.min(n + 1, findable)),
      readMs / (total + 1),
    );
    return () => window.clearInterval(id);
  }, [card.checklistReads, reading, readMs, total, findable]);
  // For the demo, a card ticked by reading also ticks while they record the
  // answer, one item every few seconds, as if it were heard as they say it.
  // The reading carries on from wherever this got to.
  const [heard, setHeard] = useState(0);
  useEffect(() => {
    if (!card.checklistReads || !recording || index !== until) return;
    const id = window.setInterval(
      () => setHeard((n) => Math.min(n + 1, findable)),
      HEARD_MS,
    );
    return () => window.clearInterval(id);
  }, [card.checklistReads, recording, index, until, findable]);
  const checked = !card.checklistReads
    ? counted
    : index > until
      ? findable
      : reading
        ? Math.max(found, heard)
        : recording || heard > 0
          ? heard
          : -1;

  // Held while the question is open. A card ticked by reading is also held
  // again on the check that follows it, before it is confirmed, if they start
  // saying more while something on it is still missing: the turn is not over
  // until they say the summary is right, and what is missing is what to say.
  const holding =
    until >= 0 &&
    (index <= until ||
      (Boolean(card.checklistReads) &&
        index === until + 1 &&
        composing &&
        checked < total));

  // Coming back, rather than pinned from the start: it drops in from the top
  // of the chat instead of snapping there, so it reads as the card they saw
  // before returning, not something new arriving. Counted so each return
  // plays it again.
  //
  // Letting go: it stays stuck at the top for one last movement, pressing down
  // and then sliding up, so it tucks back up into the chat rather than
  // vanishing in a frame.
  // Tracked during render so the frame it lets go in is already the leaving one.
  const ref = useRef<HTMLDivElement>(null);
  const [wasHolding, setWasHolding] = useState(holding);
  const [returns, setReturns] = useState(0);
  const [leaving, setLeaving] = useState(false);
  // Coming back and not yet landed: flat, without its shadow, until it lifts.
  const [landing, setLanding] = useState(false);
  if (wasHolding !== holding) {
    setWasHolding(holding);
    if (holding) {
      setReturns((n) => n + 1);
      setLeaving(false);
      setLanding(true);
    } else {
      setLeaving(true);
    }
  }

  // Whether it has actually reached the top and is stuck there, rather than
  // held but still sitting among the messages. Only then does it look pinned:
  // the pin and the shadow say it is staying put while the chat moves, which
  // is not true of a card the chat has not scrolled up to yet.
  const stuckAt = () => {
    const el = ref.current;
    let box = el?.parentElement ?? null;
    while (box && !/(auto|scroll)/.test(getComputedStyle(box).overflowY)) {
      box = box.parentElement;
    }
    return { el, box };
  };
  const [stuck, setStuck] = useState(false);
  const stickable = holding || leaving;
  useLayoutEffect(() => {
    const { el, box } = stuckAt();
    if (!stickable || !el || !box) {
      setStuck(false);
      return;
    }
    const measure = () => setStuck(isStuck(el));
    measure();
    // Again a frame later, once the chat has scrolled to its end on opening.
    const frame = requestAnimationFrame(measure);
    box.addEventListener('scroll', measure, { passive: true });
    // The window changing size, and the conversation in it growing, both move
    // the card without a scroll.
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    if (box.firstElementChild) observer.observe(box.firstElementChild);
    return () => {
      cancelAnimationFrame(frame);
      box.removeEventListener('scroll', measure);
      observer.disconnect();
    };
  }, [stickable]);

  // Letting go only shows when it was stuck. Still sitting among the
  // messages, letting go moves nothing, so there is nothing to show. Nor with
  // reduced motion, where there is no animation to end the leaving.
  useLayoutEffect(() => {
    if (!leaving) return;
    const { el, box } = stuckAt();
    const wasStuck = el && box && isStuck(el);
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!wasStuck || still) setLeaving(false);
  }, [leaving]);

  // The shadow comes back as it lifts, once it has slid into place. Straight
  // away with reduced motion, where it does not slide.
  // Whenever it is held, stuck to the top or not: in a short chat it never
  // reaches the top, and it is still the thing they are answering.
  const dimming = holding && !landing && composing;
  useEffect(() => {
    onDim?.(dimming);
    return () => onDim?.(false);
  }, [dimming, onDim]);

  useEffect(() => {
    if (!landing) return;
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const id = window.setTimeout(
      () => setLanding(false),
      still ? 0 : PIN_RETURN_DELAY_MS + PIN_RETURN_MS * PIN_RETURN_LANDED,
    );
    return () => window.clearTimeout(id);
  }, [landing]);

  return (
    // Held, it sticks once the chat scrolls up to it; it only shows as pinned
    // from then on.
    <Box
      ref={ref}
      key={returns}
      onAnimationEnd={(e) => {
        if (e.animationName === 'pinLeave') setLeaving(false);
      }}
      sx={
        leaving
          ? {
              position: 'sticky',
              top: 0,
              zIndex: 1,
              // Two beats, so it does not lose its shadow and move in the same
              // frame: first it presses down flat while the shadow fades (the
              // card's own transition, as it stops being pinned), then it
              // slides up out of the top of the chat, fading only at the end.
              animation: `pinLeave ${PIN_LEAVE_MS}ms ease-in-out forwards`,
              '@keyframes pinLeave': {
                '0%': { opacity: 1, transform: 'none' },
                '40%': { opacity: 1, transform: 'scale(0.98)' },
                '75%': { opacity: 1 },
                '100%': {
                  opacity: 0,
                  transform: 'translateY(calc(-100% - 16px)) scale(0.98)',
                },
              },
            }
          : holding
            ? {
                position: 'sticky',
                top: 0,
                zIndex: 1,
                boxShadow: (theme) =>
                  `0 0 0 100vmax ${alpha(theme.palette.text.primary, dimming ? PIN_SCRIM : 0)}`,
                transition: `box-shadow ${PIN_SCRIM_MS}ms ease-in-out`,
                // The card's own corners, or the scrim stops square around it
                // and leaves undimmed slivers at each corner.
                borderRadius: `${radius.md}px`,
                ...(returns > 0 && {
                  // The leaving backwards: down out of the top of the chat,
                  // pressed flat, then lifting as the shadow comes back.
                  animation: `pinReturn ${PIN_RETURN_MS}ms ease-in-out ${PIN_RETURN_DELAY_MS}ms backwards`,
                  '@keyframes pinReturn': {
                    '0%': {
                      opacity: 0,
                      transform: 'translateY(calc(-100% - 16px)) scale(0.98)',
                    },
                    '25%': { opacity: 1 },
                    [`${PIN_RETURN_LANDED * 100}%`]: {
                      opacity: 1,
                      transform: 'scale(0.98)',
                    },
                    '100%': { opacity: 1, transform: 'none' },
                  },
                  '@media (prefers-reduced-motion: reduce)': {
                    animation: 'none',
                  },
                }),
              }
            : undefined
      }
    >
      <FromUsCard
        {...card}
        checked={checked}
        pinned={holding && stuck && !landing}
      />
    </Box>
  );
};

const ChatV2 = ({
  questions,
  onExit,
  onLeave,
  onComplete,
  startAt = 0,
  recorded,
  sent = 0,
  replied,
  optionsOpen,
  autoPick,
  chose,
  failure,
  pushback,
  sendFailed,
  processed,
  uploaded,
  uploadedName,
  uploadState,
  sentAfter = 0,
  readingLast = false,
  desktop = false,
  recording = false,
  liveCaptions,
  intro,
  aboutKeniOpen = false,
  captionsShown = false,
  missed,
  autoRecord,
  autoMoveOn,
  typed,
  editingCardOpen,
  explainOpen,
  onExplainLink,
  reading = false,
  attaching: attachingOpen = false,
  staged = false,
  fresh = false,
  keepAnswers,
  changeAt,
  inlineMic = false,
  liveVoice = false,
  live = false,
  howToOpen = false,
  onClose,
  onReplay,
  onQuestionChange,
  bar,
  account,
  namedFrom,
  greeting,
  emptyState,
  askButton,
  keyboard = false,
}: {
  questions: ChatQuestion[];
  /**
   * Said in the middle of an empty chat, the way a chat app opens on a new
   * conversation, until anything is said. For a question with no ask of its
   * own, so the greeting is the opening.
   */
  greeting?: string;
  /**
   * What an empty chat shows in place of the conversation, from the top,
   * until anything is said: a page above the composer. For a Home whose
   * composer is the way to talk, with other things on it until then.
   */
  emptyState?: ReactNode;
  /**
   * With an empty state up, a button in place of the composer: talking is one
   * way on among the things on the page, not the page's whole bottom edge.
   * Tapped, the composer comes up as usual.
   */
  askButton?: { label: string; tone?: AskTone };
  /** Harness-only: the phone's keyboard up under the composer, as when typing. */
  keyboard?: boolean;
  onExit: () => void;
  /** The question's `leaves` answer was given: out of the chat altogether. */
  onLeave?: () => void;
  /**
   * A contextual app bar above the section caption, for a chat that is inside a
   * named flow (the benefits calculator). Omit and the chat has none, which is
   * right for the intake: the intake is not inside anything.
   */
  bar?: ReactNode;
  /** The account app bar, above the section caption. See StepScreen's own. */
  account?: Account;
  /**
   * The question that gets us their name. Until it is answered the avatar shows
   * a person icon rather than `account.initials`: the chat opens before we have
   * asked, and initials made up from a name we do not have yet would be a guess
   * on the one screen that is asking for it.
   */
  namedFrom?: string;
  /** The chat is over. Given what they answered, so an ending can go by it. */
  onComplete?: (answers: string[]) => void;
  /** Harness-only: open partway in, with the questions before it answered. */
  startAt?: number;
  /** Harness-only: open with a recording made and waiting to be sent. */
  recorded?: number;
  /** Harness-only: open with a photo chosen and waiting to be sent. */
  staged?: boolean;
  /** Harness-only: open mid-recording, orb listening and the clock running. */
  recording?: boolean;
  /**
   * Captions of a recording as it is made, as on dev: a CC switch in the
   * recording row, and the words in a card above the bar while it is on. The
   * words are this script, revealed as the clock runs. Omit for no captions.
   */
  liveCaptions?: string[];
  /**
   * Who they are talking to, at the very top of the conversation before the
   * first message, the way a messaging app introduces a chat: Keni's picture
   * and a line saying it is Keni. Scrolls with the chat.
   */
  intro?: {
    title: string;
    /** What tapping Keni opens: who Keni is, in a drawer. */
    about?: { title: string; points: string[] };
  };
  /**
   * Harness-only: start recording a beat after opening, as if the mic were
   * tapped. For a handover to show what starting to talk does.
   */
  autoRecord?: boolean;
  /** Harness-only: tap this way on a beat after opening, like I'm done. */
  autoMoveOn?: string;
  /** Harness-only: open with this typed into the field, not sent yet. */
  typed?: string;
  /**
   * Harness-only: open with the card with this label being corrected, its
   * lines in the composer.
   */
  editingCardOpen?: string;
  /** Harness-only: open with the drawer about Keni up. */
  aboutKeniOpen?: boolean;
  /** Harness-only: open with the current question's explain sheet with this label up. */
  explainOpen?: string;
  /**
   * An explain sheet's link tapped, where it has no `href` of its own: the
   * prototype shows where it would go instead.
   */
  onExplainLink?: () => void;
  /**
   * Harness-only: the reading of their first answer does not find the last
   * this many things the prompts card asks about, so they stay unticked.
   */
  missed?: number;
  /**
   * Harness-only: open with seeing what they said switched on, captions and
   * voice note text alike. Off by default, as on dev.
   */
  captionsShown?: boolean;
  /**
   * Harness-only: open on the beat between their answer and the reply, with the
   * dots standing in for the message on its way.
   *
   * The beat is 900ms in the live flow, which is long enough to read as somebody
   * reading and too short to look at. Held open, so a handover can show it.
   */
  reading?: boolean;
  /** Harness-only: open with the plus's drawer already up. */
  attaching?: boolean;
  /**
   * Opens as a conversation of its own: the questions before this one are not
   * replayed above it.
   *
   * Coming back to put one thing right, the whole intake scrolling above the
   * question is a transcript of a conversation they already finished. They are
   * here to say one thing.
   */
  fresh?: boolean;
  /**
   * Answers can be changed later, and the conversation is never cleared. Each
   * reply to a question with rows can be tapped: the rows open in a sheet with
   * the answer on the record ticked, and picking another changes the reply in
   * place, with `noted` saying so under it. The way on never clears the
   * conversation, so every earlier answer is still there to scroll back to.
   *
   * The calculator's. The intake still clears as it goes.
   */
  keepAnswers?: { noted: (answer: string) => string };
  /**
   * Harness-only, with `keepAnswers`: open scrolled to an earlier answer, by
   * question index. With `open`, its sheet is up; with `to`, it has already
   * been changed to that.
   */
  changeAt?: { at: number; open?: boolean; to?: string };
  /**
   * Record the way WhatsApp does, from the button beside the field rather than
   * the orb. With nothing typed it is a microphone; with something typed it is
   * send. While recording it is stop, and a stopped recording waits with send
   * and a bin beside it. No orb.
   */
  inlineMic?: boolean;
  /**
   * The calculator's composer: a card with the text on top and its controls
   * under it, and one voice button. The mic talks it through: their words show
   * as they go and send on a pause. Stop puts them in the box to edit first.
   * Typed, the mic turns into send.
   */
  liveVoice?: boolean;
  /** Harness-only: with liveVoice, open mid live conversation. */
  live?: boolean;
  /** Harness-only: with liveVoice, open with How to use up. */
  howToOpen?: boolean;
  /**
   * Somewhere to close to. Renders a way out of the conversation, which is only
   * true when it was opened on top of a screen that is still there.
   */
  onClose?: () => void;
  /** Harness-only: notes already sent against the current question. */
  sent?: number;
  /** Harness-only: a typed reply already given to the current question. */
  replied?: string;
  /** Harness-only: open with the current question's options sheet up. */
  optionsOpen?: boolean;
  /**
   * Harness-only: mark this answer as picked on the current question's menu a
   * beat after opening, and nothing else. The menu folds down to it, and the
   * chat does not move on, so a handover can show just the fold.
   */
  autoPick?: string;
  /** Harness-only: the option already chosen, so Next reads as live. */
  chose?: string;
  /** Harness-only: open with the last thing they handed over having failed. */
  failure?: FailureKind;
  /** Harness-only: a reply that came back asking for more, instead of moving on. */
  pushback?: string;
  /** Harness-only: the typed reply is sitting there unsent. */
  sendFailed?: boolean;
  /** Harness-only: they answered by handing a file over rather than typing. */
  uploaded?: AttachmentKind;
  /**
   * Harness-only: what that file is called. Without it every handed-over
   * document is the discharge summary, which is the wrong file on any question
   * that offers a different one.
   *
   * Several names hand several files over, one message each. A question that
   * asks for paperwork rather than for one document is answered by whatever
   * they happen to have, and one attachment in the transcript says the offer
   * only takes one.
   */
  uploadedName?: string | string[];
  /** Harness-only: what state that file is in. */
  uploadState?: AttachmentState;
  /**
   * Drawn on a desktop rather than a phone, so the status bar and the home
   * indicator go: they are phone chrome, and there is no phone. Everything else
   * is the same conversation.
   */
  desktop?: boolean;
  /**
   * Harness-only: notes sent after the upload, to show that nothing waited for
   * it. Processing a file does not stop the conversation.
   */
  sentAfter?: number;
  /**
   * Harness-only: they have pressed the way on and we have read what they sent.
   * The offers and the way on are spent, so they are not drawn: reading only
   * starts once they say they are done, which is the only point a transcription
   * or extraction failure can surface.
   */
  processed?: boolean;
  /** Harness-only: the last thing they sent is still being read. */
  readingLast?: boolean;
  /**
   * Which question the conversation is on, by id. The whole chat is one screen
   * to whatever is hosting it, so a stage that wants to say which question is
   * showing has no other way to know.
   */
  onQuestionChange?: (id: string) => void;
  /**
   * Harness-only: hands the stage a way to replay the bottom bar's change.
   *
   * The movement happens once, on the way past, so there was no way to look at
   * it twice. Calling this drops the next question's bar in, and calling it
   * again brings the current one back, without answering anything.
   *
   * A handle rather than a button in the transcript. Drawn in the conversation
   * it looked like something the app offers, and it sat under the orb.
   */
  onReplay?: (replay: () => void) => void;
}) => {
  const [index, setIndex] = useState(startAt);
  const [asking, setAsking] = useState(false);
  const [transcript, setTranscript] = useState<Msg[]>(() => {
    const msgs: Msg[] = [];
    // The way the conversation came, which only differs from a straight run
    // where an answer branched.
    const path = pathTo(questions, startAt);
    let from = fresh ? path.length - 1 : 0;
    path.forEach((i, at) => {
      // Kept answers still start a replay at a restart: the turns before it
      // are a finished subject (the paperwork), not answers to come back to.
      // Only the live way on stops clearing.
      if (questions[i]?.restart && at >= from) from = at;
    });
    for (const i of path.slice(from)) {
      const question = questions[i];
      if (!question) break;
      // Rows belong to the live question. Left in the history they read as
      // offers that are still open when they are not.
      msgs.push(
        ...askMsgs(question),
        // A question being read is answered: its rows have been used, and the
        // dots below them are the reply on its way.
        ...(i === startAt && !processed && !reading
          ? asideMsgs(question, Boolean(replied))
          : []),
      );
      // A turn with nothing to answer (the reading) gets no reply.
      const unanswerable =
        question.mode === 'rows' &&
        !question.options?.length &&
        !question.asides?.some(isAnswerRow);
      // A turn that read each thing as it arrived, opened after: what they
      // handed over, then the way on they tapped.
      if (i !== startAt && question.readsEach) {
        msgs.push(
          { from: 'user', text: 'Voice note', seconds: 12 },
          {
            from: 'user',
            text: '',
            attachment: 'photo',
            attachmentState: 'sent',
          },
          { from: 'user', text: 'Voice note', seconds: 21 },
        );
      }
      // Opened mid-wait on a typed answer: the reply is what is being read,
      // so it is on screen above the label. Typed only, the calculator's, so
      // onboarding's frames are unchanged.
      if (
        i === startAt &&
        reading &&
        question.typed &&
        question.reading &&
        question.answer
      ) {
        msgs.push({ from: 'user', text: question.answer });
      }
      if (i !== startAt && !unanswerable) {
        // A spoken question was answered by speaking.
        msgs.push(
          // Unless it offered replies to tap: then the reply is what they
          // tapped, not a recording.
          question.mode === 'say' &&
            question.voice !== false &&
            !question.typed &&
            !question.quickReplies &&
            !question.attachRows
            ? {
                from: 'user',
                text: 'Voice note',
                seconds: 47,
                // The captions script is what the first answer said.
                ...(liveCaptions && question.id === 'help'
                  ? { transcript: liveCaptions.join(' ') }
                  : {}),
              }
            : {
                from: 'user',
                text: question.answer ?? question.options?.[0] ?? 'Yes',
                ...(keepAnswers ? { qi: i } : {}),
              },
        );
      }
    }
    const notes: Msg[] = Array.from({ length: sent }, (_, i) => ({
      from: 'user' as const,
      text: 'Voice note',
      seconds: 12 + i * 9,
      // Sent against the open ask, the captions script is what it said.
      ...(liveCaptions && questions[startAt]?.id === 'help' && i === 0
        ? { transcript: liveCaptions.join(' ') }
        : {}),
    }));
    const opening: Msg[] = replied
      ? [...notes, { from: 'user', text: replied }]
      : [...notes];
    // Whatever the last thing they sent was, typed or spoken.
    const last = opening.length - 1;
    if (sendFailed && last >= 0) {
      opening[last] = { ...opening[last], failed: true };
    }
    const trailing: Msg[] = [];
    if (uploaded) {
      // One message per file. A photo carries no name, so the list only sets
      // how many there are.
      const files = Array.isArray(uploadedName)
        ? uploadedName
        : [uploadedName ?? INTAKE_UPLOADS.document];
      files.forEach((name) => {
        trailing.push({
          from: 'user',
          text: uploaded === 'document' ? name : '',
          attachment: uploaded,
          attachmentState: uploadState ?? 'sent',
        });
      });
      // Sent while it was still being read, so the transcript shows that the
      // conversation carried on over the top of it.
      for (let i = 0; i < sentAfter; i += 1) {
        trailing.push({
          from: 'user',
          text: 'Voice note',
          seconds: 15 + i * 11,
        });
      }
    }
    if (readingLast) {
      const all = [...opening, ...trailing];
      const at = all.length - 1;
      if (at >= 0) {
        if (at < opening.length) opening[at] = { ...opening[at], reading: 1 };
        else
          trailing[at - opening.length] = {
            ...trailing[at - opening.length],
            reading: 1,
          };
      }
    }
    if (failure) trailing.push({ from: 'keni', text: '', failure });
    if (pushback) trailing.push({ from: 'keni', text: pushback });
    return above(msgs, [...opening, ...trailing]);
  });

  // The drawer Keni's picture opens: who Keni is, and why a llama.
  const [aboutKeni, setAboutKeni] = useState(aboutKeniOpen);

  // The sheet a quieter row opens to explain something, like How we use AI.
  const [explaining, setExplaining] = useState<string | undefined>(explainOpen);

  // A card being corrected in the composer, by its place in the transcript.
  // A card whose lines are answer rows opens them in a drawer instead.
  const [factsCard, setFactsCard] = useState<FromUs | null>(null);

  // The pencil puts its lines in the field, the way a messaging app edits a
  // message, and sending is the correction. No drawer: one place to type.
  const [editingCard, setEditingCard] = useState<{
    at: number;
    label: string;
  } | null>(() => {
    if (!editingCardOpen) return null;
    const at = transcript.findLastIndex(
      (m) => m.card?.label === editingCardOpen,
    );
    return at < 0 ? null : { at, label: editingCardOpen };
  });

  // An answered question with its rows attached, open again in place to
  // change it, by its index. The rest change in the sheet below.
  const [reopenedMenu, setReopenedMenu] = useState<number | null>(() =>
    changeAt?.open && questions[changeAt.at]?.attachRows ? changeAt.at : null,
  );

  // The sheet for changing an earlier answer: which question, and what is
  // ticked in it while it is open.
  const [changing, setChanging] = useState<{
    qi: number;
    picked: string[];
  } | null>(() => {
    if (!changeAt?.open) return null;
    const question = questions[changeAt.at];
    // Rows attached under the ask open again in place, not in a sheet.
    if (question?.attachRows) return null;
    const was = question?.answer ?? question?.options?.[0];
    return { qi: changeAt.at, picked: was ? [was] : [] };
  });

  // Reported rather than derived, because the conversation is the screen: the
  // host frame does not change as the questions go by.
  useEffect(() => {
    const id = questions[index]?.id;
    if (id) onQuestionChange?.(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);
  const [picked, setPicked] = useState<string | null>(chose ?? null);
  const [text, setText] = useState(typed ?? '');
  // Half the chat's height: as tall as the text being edited gets before it
  // scrolls. Measured when editing starts, from the screen it is on.
  const [editMax, setEditMax] = useState<number>();
  const editRef = useRef<HTMLTextAreaElement>(null);
  // Whether what is typed has wrapped past one line. A pill that tall goes
  // lozenge, so the field takes the captions card's corners, the same as when
  // it holds a card's lines.
  const fieldRef = useRef<HTMLDivElement>(null);
  const [wrapped, setWrapped] = useState(false);
  useLayoutEffect(() => {
    const field = fieldRef.current?.querySelector('textarea');
    if (!field) return;
    const measure = () => {
      const line = parseFloat(getComputedStyle(field).lineHeight);
      setWrapped(Number.isFinite(line) && field.offsetHeight > line * 1.5);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(field);
    return () => observer.disconnect();
  });
  // Opened by the harness, nobody tapped anything, so focus is not taken: on a
  // flows page that would scroll the page to the mini.
  const editFromHarness = useRef(Boolean(editingCardOpen));
  useEffect(() => {
    if (!editingCard) return;
    const screen = scrollerRef.current?.parentElement;
    if (screen) setEditMax(Math.round(screen.clientHeight / 2));
    if (editFromHarness.current) {
      editFromHarness.current = false;
      return;
    }
    // The cursor at the end of the text, and the end in view, so the last
    // line is what they see rather than the first.
    const el = editRef.current;
    if (!el) return;
    el.focus({ preventScroll: true });
    el.setSelectionRange(el.value.length, el.value.length);
    el.scrollTop = el.scrollHeight;
  }, [editingCard]);

  // The lines of a card being corrected, in the card above the bar.
  const [editText, setEditText] = useState(() =>
    editingCardOpen
      ? (transcript
          .findLast((m) => m.card?.label === editingCardOpen)
          ?.card?.items?.join('\n') ?? '')
      : '',
  );

  const [listening, setListening] = useState(recording);
  // The plus is open. What it offers is the two things this flow takes.
  const [attaching, setAttaching] = useState(attachingOpen);
  // How to use, opened from the calculator's composer.
  const [howTo, setHowTo] = useState(howToOpen);
  // Seconds recorded and not yet sent. The draft, in other words.
  const [draft, setDraft] = useState<number | undefined>(recorded);
  // A photo chosen from the plus and not yet sent. Seen before it goes, the
  // same as a recording, so sending it is a decision.
  const [stagedPhoto, setStagedPhoto] = useState(staged);
  const showStaged = stagedPhoto && draft === undefined && !listening;
  // The staged photo opened full screen, to check it properly before it goes.
  const [viewingStaged, setViewingStaged] = useState(false);
  const sendStaged = () => {
    setStagedPhoto(false);
    upload('photo');
  };
  // Seconds recorded so far. Ticks while the orb is listening, so the counter
  // belongs to the recording rather than being a number sitting there.
  const [elapsed, setElapsed] = useState(recording ? 12 : 0);
  const [captionsOn, setCaptionsOn] = useState(captionsShown);
  // About two and a half words a second, which is how fast people talk.
  const heard = liveCaptions?.slice(0, Math.floor((draft ?? elapsed) * 2.5));

  useEffect(() => {
    if (!listening) return;
    const id = window.setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => window.clearInterval(id);
  }, [listening]);

  const [thinking, setThinking] = useState(reading);
  // What they said is being read and said so in the chat: nothing can be
  // answered until the reply lands, so the composer is off until then.
  // The question just answered says what to show while it is read, if
  // anything. The index only moves once the next turn lands, so during the
  // wait it is still the answered one.
  const readingNow = questions[index]?.reading;
  const reading_ = thinking && Boolean(readingNow);
  // Nothing typed yet, so the composer offers to talk rather than send.
  const micInFieldFor = (q: ChatQuestion) =>
    inlineMic &&
    q.voice !== false &&
    !text.trim() &&
    !stagedPhoto &&
    !editingCard;
  // Talking it through live: the bar is the conversation, not a composer, and
  // what they say lands as text, the same as typing it.
  const [talking, setTalking] = useState(live && liveVoice);
  // Turns only play once they have tapped in, so a frame opened live holds
  // still on the canvas.
  const [playing, setPlaying] = useState(false);
  // Keni is saying the next turn: the orb talks, and nothing is heard over it.
  const speaking = thinking || reading_;
  // What is being read while the reading lines run: the last thing they sent.
  const lastSent = reading_
    ? transcript.findLastIndex((m) => m.from === 'user' && !m.aside)
    : -1;
  const think = useRef<number | undefined>(undefined);
  // Everything they answered, in order, handed to onComplete with the end.
  const answers = useRef<string[]>([]);
  useEffect(() => () => window.clearTimeout(think.current), []);

  /**
   * The next question, after a beat of dots.
   *
   * The answer lands straight away and the question that follows it does not:
   * every question used to arrive in the same frame as the answer to the last
   * one, which read as a form validating rather than somebody replying.
   *
   * The index moves with the message, so the bar underneath belongs to the
   * question on screen rather than to one that has not been asked yet.
   */
  const arrive = (msgs: Msg[], next: number) => {
    setThinking(true);
    window.clearTimeout(think.current);
    think.current = window.setTimeout(() => {
      setThinking(false);
      setTranscript((prev) => [...prev, ...msgs]);
      setIndex(next);
      if (!questions[next]) onComplete?.(answers.current);
    }, readingNow?.ms ?? THINK_MS);
  };

  // Scrolls the transcript itself, not the message: scrollIntoView moves every
  // scrollable ancestor too, so on the flows page each mini dragged the whole
  // page to wherever its chat ended.
  const scrollerRef = useRef<HTMLDivElement>(null);
  // The pinned card's scrim, carried up over the bars above the chat.
  const [dimmed, setDimmed] = useState(false);
  const stackRef = useRef<HTMLDivElement>(null);
  // The harness opens on an earlier answer, so it does not follow the bottom.
  const toBottom = () => {
    const el = scrollerRef.current;
    if (el && !changeAt) el.scrollTop = el.scrollHeight;
  };
  useEffect(toBottom, [transcript, thinking]);

  // Harness-only: open with the changed question at the top of the screen.
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    const stack = stackRef.current;
    if (!changeAt || !el || !stack) return;
    const reply = transcript.findIndex(
      (m) => m.qi === changeAt.at && m.from === 'user',
    );
    const row = stack.children[reply - 1] as HTMLElement | undefined;
    if (row) el.scrollTop = row.offsetTop - stack.offsetTop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const q: ChatQuestion | undefined = questions[index];
  // The sheet open from one of its quieter rows, if any.
  const explained = questions
    .flatMap((x) => x.explain ?? [])
    .find((e) => e.label === explaining);
  const done = !q;

  // Only where nothing above is carrying it. With an account bar the section
  // IS the bar's title, so a caption here would be the same line twice.
  const section = account ? undefined : q?.section;

  // Which of a pick-several's rows are ticked. Cleared when the question
  // changes: the ticks belong to the question, and a row on the next one would
  // otherwise open already answered.
  const [chosen, setChosen] = useState<string[]>([]);
  useEffect(() => setChosen([]), [index]);

  // A question that reads each thing as it arrives: the message is marked as
  // being read, and a while later it is done. A file is read once it has
  // finished leaving the phone.
  const readSeq = useRef(0);
  const readEach = (msg: Msg): Msg => {
    if (!q?.readsEach) return msg;
    readSeq.current += 1;
    const id = readSeq.current;
    window.setTimeout(
      () =>
        setTranscript((prev) =>
          prev.map((m) =>
            m.reading === id ? { ...m, reading: undefined } : m,
          ),
        ),
      READ_EACH_MS + (msg.attachment ? 1200 : 0),
    );
    return { ...msg, reading: id };
  };

  // A voice note as it is sent, carrying what the captions heard of it.
  const said = (seconds: number): Msg => ({
    from: 'user',
    text: 'Voice note',
    seconds,
    ...(liveCaptions
      ? {
          transcript: liveCaptions
            .slice(0, Math.floor(seconds * 2.5))
            .join(' '),
        }
      : {}),
  });

  const send = (seconds: number) => {
    if (!q) return;
    setDraft(undefined);
    setListening(false);
    // A question that stays open takes the answer and stays put.
    if (q.multi) {
      setTranscript((prev) => [
        ...above(prev, [readEach(said(seconds))]),
        ...heldBack(prev, q),
      ]);
      return;
    }
    const next = index + 1;
    setTranscript((prev) => [...prev, { ...said(seconds) }]);
    // With its rows. Only the ask used to be pushed here, so the way on never
    // arrived for anyone who answered by talking: the first recording was
    // followed by a question about documents and nothing saying how to move on
    // from it.
    arrive(
      [...askMsgs(questions[next]), ...asideMsgs(questions[next], false)],
      next,
    );
  };

  // Trying an upload again puts it back on the wire, and it walks the same beats
  // as a fresh one.
  const retryUpload = () => {
    setTranscript((prev) =>
      prev.map((m) =>
        m.attachmentState === 'failed'
          ? { ...m, attachmentState: 'uploading' as const }
          : m,
      ),
    );
    settleUpload();
  };

  // Resend clears the failure: the prototype shows the state, it does not
  // simulate a second attempt succeeding or failing again.
  const resend = () =>
    setTranscript((prev) =>
      prev.map((m) => (m.failed ? { ...m, failed: false } : m)),
    );

  // Puts a changed answer in place of the reply it replaces, with a line under
  // it saying so. A second change replaces the line rather than adding one.
  const applyChange = (qi: number, picked: string[]) => {
    if (!keepAnswers || picked.length === 0) return;
    setTranscript((prev) => {
      const was = prev
        .filter((m) => m.qi === qi && m.from === 'user')
        .map((m) => m.text);
      if (was.join('|') === picked.join('|')) return prev;
      const first = prev.findIndex((m) => m.qi === qi && m.from === 'user');
      if (first === -1) return prev;
      const rest = prev.filter(
        (m, i) =>
          i < first ||
          (!(m.qi === qi && m.from === 'user') && m.notedFor !== qi),
      );
      const before = rest.slice(0, first);
      const after = rest.slice(first);
      return [
        ...before,
        ...picked.map((text) => ({ from: 'user' as const, text, qi })),
        {
          from: 'keni' as const,
          text: keepAnswers.noted(picked.join(', ')),
          notedFor: qi,
        },
        ...after,
      ];
    });
  };

  // Where answers are kept, an answered menu opens again to change it: the
  // same rows, not a sheet. Not the menu still being answered.
  const menuChange = (menuFor: string) => {
    const mi = questions.findIndex((x) => x.id === menuFor);
    if (!keepAnswers || mi === index) return {};
    return {
      reopened: reopenedMenu === mi,
      onReopen: () => setReopenedMenu(mi),
      onChange: (label: string) => {
        applyChange(mi, [label]);
        setReopenedMenu(null);
      },
    };
  };

  // Harness-only: the change already made.
  useEffect(() => {
    if (changeAt?.to) applyChange(changeAt.at, [changeAt.to]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const moveOn = (said?: string) => {
    if (said) answers.current.push(said);
    const next = nextFrom(questions, index, said);
    const opening: Msg[] = [
      ...askMsgs(questions[next]),
      ...asideMsgs(questions[next], false),
    ];
    // A way on tapped from a chip is something they said, so it lands as
    // their reply and the next turn arrives after it.
    if (said) {
      setTranscript((prev) => [
        ...prev.filter((m) => !m.aside),
        { from: 'user', text: said },
      ]);
      arrive(opening, next);
      return;
    }
    if (keepAnswers) {
      setTranscript((prev) => [...prev.filter((m) => !m.aside), ...opening]);
      setIndex(next);
      if (!questions[next]) onComplete?.(answers.current);
      return;
    }
    setTranscript((prev) =>
      questions[next]?.restart
        ? opening
        : [...prev.filter((m) => !m.aside), ...opening],
    );
    setIndex(next);
    if (!questions[next]) onComplete?.(answers.current);
  };

  // It goes, then it is being read, then it is done. Nothing is blocked while any
  // of that happens: they can keep sending over the top of it.
  const settleUpload = () => {
    const advance = (from: AttachmentState, to: AttachmentState, ms: number) =>
      window.setTimeout(
        () =>
          setTranscript((prev) =>
            prev.map((m) =>
              m.attachmentState === from ? { ...m, attachmentState: to } : m,
            ),
          ),
        ms,
      );
    // Straight to sent: nothing is read until they say they are done.
    advance('uploading', 'sent', 1200);
  };

  // Handing a file over answers the question the same way words do: it lands in
  // the transcript, and a question that stays open stays open.
  const upload = (kind: AttachmentKind, file?: string) => {
    if (!q) return;
    const msg: Msg = readEach({
      from: 'user',
      // What the offer says it hands over, and the discharge summary only where
      // nothing says otherwise.
      text: kind === 'document' ? (file ?? INTAKE_UPLOADS.document) : '',
      attachment: kind,
      attachmentState: 'uploading',
    });
    settleUpload();
    if (q.multi) {
      setTranscript((prev) => [...above(prev, [msg]), ...heldBack(prev, q)]);
      return;
    }
    const next = nextFrom(questions, index);
    setTranscript((prev) => [...prev, msg]);
    arrive(
      [...askMsgs(questions[next]), ...asideMsgs(questions[next], false)],
      next,
    );
  };

  const answer = (value: string) => {
    if (!value || !q) return;
    answers.current.push(value);
    // Turning down the AI: nothing here can go on without it, so they leave
    // for the onboarding that does not use it.
    if (q.leaves && value === q.leaves && onLeave) {
      onLeave();
      return;
    }
    // A correction to a card: their edited lines go as their message, and the
    // card comes back with them in it. The question stays where it is, and the
    // old card stays in the history as it was.
    if (editingCard) {
      const was = transcript[editingCard.at]?.card;
      const items = value
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);
      setTranscript((prev) => [
        ...prev,
        { from: 'user', text: value, edit: true },
        { from: 'keni', text: "Thanks, I've changed that." },
        ...(was
          ? [{ from: 'keni' as const, text: '', card: { ...was, items } }]
          : []),
      ]);
      setEditingCard(null);
      setEditText('');
      return;
    }
    if (q.multi) {
      // Ticking again takes it back. A checkbox that only goes one way is a
      // worse promise than no checkbox at all, so untapping pulls the answer
      // out of the transcript as well as clearing the tick.
      if (chosen.includes(value)) {
        setChosen((prev) => prev.filter((v) => v !== value));
        setTranscript((prev) => {
          const at = prev.findIndex(
            (m) => m.from === 'user' && m.text === value,
          );
          return at === -1
            ? prev
            : [...prev.slice(0, at), ...prev.slice(at + 1)];
        });
        return;
      }
      setChosen((prev) => [...prev, value]);
      setTranscript((prev) => [
        ...above(prev, [
          readEach({
            from: 'user',
            text: value,
            ...(keepAnswers ? { qi: index } : {}),
          }),
        ]),
        ...heldBack(prev, q),
      ]);
      setText('');
      return;
    }
    // A typed answer that will not do is said back, and the question stays
    // open with its rows. A row is never checked: it is one of our answers.
    const problem = q.asides?.some((a) => a.label === value)
      ? undefined
      : q.check?.(value);
    if (problem) {
      setTranscript((prev) => [
        ...prev,
        { from: 'user', text: value },
        { from: 'keni', text: problem },
      ]);
      setText('');
      return;
    }
    const next = nextFrom(questions, index, value);
    // The rows the question was offering go with it. They belong to a live
    // question: left in the history they read as still open, and they are still
    // tappable, so an answer row from the fork would answer whatever came next.
    // Taking the way on already did this; answering did not.
    // A reply to one of the rows can be changed later, so it knows its
    // question.
    const changeable =
      keepAnswers && q.asides?.some((a) => isAnswerRow(a) && a.label === value);
    setTranscript((prev) => [
      ...prev.filter((m) => !m.aside),
      { from: 'user', text: value, ...(changeable ? { qi: index } : {}) },
    ]);
    setPicked(null);
    setText('');
    setListening(false);
    // What the next question says and offers, with it. Only the ask was pushed
    // here, so a question walked into arrived without its rows while the same
    // question opened directly had them: the fork's answers were invisible to
    // anyone who got there by answering.
    arrive(
      [...askMsgs(questions[next]), ...asideMsgs(questions[next], false)],
      next,
    );
  };

  // Which question's bar is on screen. It lags the live question by however
  // long the exit takes, so the bar on its way out is still the one that was
  // being answered.
  //
  // Only a change of kind animates. Two open questions in a row are the same
  // bar with a new placeholder, and sliding that out and back in would say
  // something changed when nothing did.
  const [demoNext, setDemoNext] = useState(false);
  // By question id, not index: the bar on its way out belongs to the question
  // just answered, which is not the one the index is on.
  const [unlocked, setUnlocked] = useState<string[]>([]);
  const isUnlocked = (question?: ChatQuestion) =>
    Boolean(question && unlocked.includes(question.id));
  const footerQuestion = demoNext ? (questions[index + 1] ?? q) : q;
  const [shownFooter, setShownFooter] = useState(footerQuestion);
  // Whether the bar on screen is the unlocked one. Tracked beside shownFooter
  // rather than read live: unlocking changes the bar of the question already
  // showing, so without this the comparison below sees no change and the input
  // appears instead of arriving.
  const [shownUnlocked, setShownUnlocked] = useState(false);
  const [footerIn, setFooterIn] = useState(true);

  useEffect(() => {
    const next = footerKind(footerQuestion, isUnlocked(footerQuestion));
    const shown = footerKind(shownFooter, shownUnlocked);
    if (
      next === shown ||
      // Typed to spoken is the same composer gaining the orb, so it swaps in
      // place and the bar grows rather than dropping away. See ORB_ARRIVAL.
      (ORB_ARRIVAL === 'grow' && isComposer(next) && isComposer(shown))
    ) {
      if (footerQuestion?.id !== shownFooter?.id) {
        setShownFooter(footerQuestion);
        setShownUnlocked(isUnlocked(footerQuestion));
      }
      return;
    }
    setFooterIn(false);
  }, [footerQuestion, shownFooter, shownUnlocked, unlocked]);

  // The harness tapping the mic.
  useEffect(() => {
    if (!autoRecord) return;
    const id = window.setTimeout(() => setListening(true), 1200);
    return () => window.clearTimeout(id);
  }, [autoRecord]);

  // The harness marking an answer on the menu, without answering.
  const [demoPicked, setDemoPicked] = useState<string>();
  useEffect(() => {
    if (!autoPick) return;
    const id = window.setTimeout(() => setDemoPicked(autoPick), 1200);
    return () => window.clearTimeout(id);
  }, [autoPick]);

  // The harness tapping the way on. Through a ref, since moveOn closes over
  // the question it was made for and the timer outlives that render.
  const moveOnRef = useRef(moveOn);
  moveOnRef.current = moveOn;
  useEffect(() => {
    if (!autoMoveOn) return;
    const id = window.setTimeout(() => moveOnRef.current(autoMoveOn), 1500);
    return () => window.clearTimeout(id);
  }, [autoMoveOn]);

  // Handed up once. The stage keeps it for as long as the chat is mounted, and a
  // remount hands over a fresh one.
  useEffect(() => {
    onReplay?.(() => setDemoNext((v) => !v));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const settleFooter = () => {
    if (footerIn) return;
    setShownFooter(footerQuestion);
    setShownUnlocked(isUnlocked(footerQuestion));
    setFooterIn(true);
  };

  /**
   * Tapping 'Type my name instead'. The composer's slide is a mount animation
   * on the wrapper below, not anything driven from here.
   *
   * Two things were tried first and neither moved. footerIn puts the container
   * through its transition, but that transition is settled by the container's
   * own onTransitionEnd and there is no outgoing bar here to settle — the
   * question was showing none — so the handler fired instantly and put the bar
   * up before it painted. Adding an `animation` to the container instead did
   * nothing either: it was already mounted, and a keyframe added to a mounted
   * element does not replay. The reveal has to BE a mount.
   */
  const openInput = (id: string) => {
    setUnlocked((prev) => [...prev, id]);
    setShownUnlocked(true);
  };

  // The room the orb needs above the bar comes and goes with the bar, so where
  // the bottom of the transcript is changes when the bar settles. Without this
  // the last row stays where it was and ends up under the orb.
  useEffect(toBottom, [shownFooter]);

  // The bottom bar for one question: one control for a closed question, the orb
  // and the input for an open one, with the orb straddling the top edge.
  //
  // Takes the question rather than reading the live one, because the bar that is
  // leaving belongs to the question just answered.
  // Nothing to answer once the last question is: the conversation is over, and
  // a chat that ends on an answer rather than a way on would otherwise draw a
  // composer for a question that does not exist.
  // Live, a pause ends their turn, so each answer goes without a send. Here the
  // pause is a timer, standing in for the speech service hearing them stop.
  useEffect(() => {
    // Not where the answers are attached: those are tapped, and sending one
    // for them would skip the choice they came to make.
    if (!playing || !talking || speaking || !q?.answer || q.attachRows) {
      return;
    }
    const id = window.setTimeout(() => answer(q.answer!), LIVE_TURN_MS);
    return () => window.clearTimeout(id);
    // answer is a fresh closure each render; the turn is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, talking, speaking, index]);

  // What they're saying, word by word as it's heard, so they can see it before
  // it sends itself. Before anyone taps in, a frame shows the whole answer.
  const [liveHeard, setLiveHeard] = useState(0);
  useEffect(() => {
    setLiveHeard(0);
    if (!playing || !talking || speaking) return;
    const id = window.setInterval(() => setLiveHeard((n) => n + 1), 400);
    return () => window.clearInterval(id);
  }, [playing, talking, speaking, index]);
  const liveWords = (q?.answer ?? '').split(' ').filter(Boolean);
  const liveSaid = (playing ? liveWords.slice(0, liveHeard) : liveWords).join(
    ' ',
  );

  // Whose turn it is while talking: theirs, and the wave moves, or ours, and
  // it lies flat.
  const liveState = speaking ? 'Talking' : 'Listening';
  const hearing = !speaking;
  // Their words on the one line the composer has, so the card never grows:
  // the latest few, as a line of captions would.
  const liveLine = liveSaid.split(' ').slice(-LIVE_LINE_WORDS).join(' ');

  const footerBar = (question?: ChatQuestion) =>
    !question ||
    footerKind(question, shownUnlocked) === 'none' ? null : question &&
      question.mode !== 'say' ? (
      <Box
        sx={{
          px: 3,
          pt: 1.5,
          pb: 2,
          borderTop: 1,
          borderColor: 'custom.neutral.200',
          bgcolor: 'background.paper',
        }}
      >
        <Stack spacing={2}>
          {question.mode === 'pick' ? (
            <Stack spacing={1.5}>
              {question.options?.map((o) => (
                <OptionCard
                  key={o}
                  control='radio'
                  selected={picked === o}
                  label={o}
                  onClick={() => setPicked(o)}
                />
              ))}
            </Stack>
          ) : (
            <Stack spacing={2}>
              {question.fields?.map((f) => (
                <TextField
                  key={f.label}
                  label={f.label}
                  defaultValue={f.value}
                  fullWidth
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              ))}
            </Stack>
          )}

          <Button
            variant='primary'
            size='lg'
            isFullWidth
            ariaLabel='Next'
            isDisabled={question.mode === 'pick' && !picked}
            onPress={() =>
              answer(
                question.mode === 'pick'
                  ? (picked ?? '')
                  : (question.fields?.[0]?.value ?? 'Done'),
              )
            }
          >
            Next
          </Button>
        </Stack>
      </Box>
    ) : (
      <Box
        sx={{
          position: 'relative',
          px: 3,
          pb: 2,
          // Room for the orb to straddle the edge, when it does.
          // And gives the room back while the orb is put away for a draft.
          pt:
            inlineMic || question.voice === false || draft !== undefined
              ? 1.5
              : '52px',
          // The bar grows to make room for the orb rather than jumping to it.
          ...(ORB_ARRIVAL === 'grow' && {
            // Closing up waits for the orb to have gone; opening does not.
            transition: `padding-top .28s cubic-bezier(0.22, 1, 0.36, 1) ${
              draft !== undefined ? '.26s' : '0s'
            }`,
          }),
          borderTop: 1,
          borderColor: 'custom.neutral.200',
          bgcolor: 'background.paper',
        }}
      >
        {question.voice !== false && !inlineMic && (
          <Box
            // Put away while a recording is waiting to be sent: they send it or
            // delete it first, so nothing they said is lost or sent by
            // accident. It comes back the way it arrived once they have.
            onClick={() => {
              if (draft !== undefined) return;
              setListening((v) => {
                if (!v) setElapsed(0);
                return !v;
              });
            }}
            sx={{
              position: 'absolute',
              top: 0,
              left: '50%',
              // Its own arrival, a beat behind the bar's. Riding the bar as one
              // block made the orb part of the furniture; coming up after it
              // lands makes it the thing you notice, which is what it is for.
              transform: footerIn
                ? 'translate(-50%, -50%)'
                : 'translate(-50%, 28px)',
              transition: 'transform .24s ease',
              transitionDelay: footerIn ? '.09s' : '0s',
              // Scales up from nothing once the bar has started growing, with a
              // little overshoot so it lands rather than just appears. A mount
              // animation, so it runs whenever the orb arrives.
              ...(ORB_ARRIVAL === 'grow' && {
                '@keyframes orbIn': {
                  from: {
                    transform: 'translate(-50%, -50%) scale(0)',
                    opacity: 0,
                  },
                  to: {
                    transform: 'translate(-50%, -50%) scale(1)',
                    opacity: 1,
                  },
                },
                animation:
                  'orbIn .36s cubic-bezier(0.34, 1.56, 0.64, 1) .12s both',
              }),
              cursor: draft !== undefined ? 'default' : 'pointer',
            }}
          >
            <Box
              sx={{
                transform: draft !== undefined ? 'scale(0)' : 'scale(1)',
                opacity: draft !== undefined ? 0 : 1,
                // Its arrival in reverse: out first, pulling back a little
                // before it goes, then the bar closes up after it. Back in with
                // the overshoot it arrived on, once the bar has made room.
                transition:
                  draft !== undefined
                    ? 'transform .3s cubic-bezier(0.36, 0, 0.66, -0.56), opacity .2s ease .1s'
                    : 'transform .36s cubic-bezier(0.34, 1.56, 0.64, 1) .12s, opacity .2s ease .12s',
              }}
            >
              <KeniOrb size={80} state={listening ? 'listening' : 'idle'} />
            </Box>
          </Box>
        )}

        {/* Grows the bar to make room, and the photo scales up into it from
            the corner the plus is in. Reversed when it is taken away. */}
        <Collapse in={showStaged} timeout={STAGE_MS} easing={STAGE_EASE}>
          <Box sx={{ pb: 1.5 }}>
            <Box
              role='button'
              aria-label='View photo'
              onClick={() => setViewingStaged(true)}
              sx={{
                position: 'relative',
                width: 72,
                cursor: 'pointer',
                transformOrigin: 'bottom left',
                transform: showStaged ? 'scale(1)' : 'scale(0.85)',
                opacity: showStaged ? 1 : 0,
                transition: `transform ${STAGE_MS}ms ${STAGE_EASE}, opacity ${STAGE_MS}ms ${STAGE_EASE}`,
                height: 72,
                borderRadius: `${radius.md}px`,
                overflow: 'hidden',
                backgroundImage: 'url(/vector/photo-placeholder.svg)',
                backgroundSize: 'cover',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'custom.neutral.500',
              }}
            >
              <Icon icon={'id-card' as never} fontSize={24} />
              <IconButton
                aria-label='Remove photo'
                size='small'
                onClick={(e) => {
                  e.stopPropagation();
                  setStagedPhoto(false);
                }}
                sx={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  width: 20,
                  height: 20,
                  bgcolor: 'custom.charcoal',
                  color: 'common.white',
                  '&:hover': { bgcolor: 'custom.charcoal' },
                }}
              >
                <Icon icon={'xmark' as never} fontSize={10} />
              </IconButton>
            </Box>
          </Box>
        </Collapse>
        <Stack
          spacing={1.5}
          // Untouchable while what they said is being read: the field fades,
          // and the button they just pressed turns into a spinner.
          aria-disabled={reading_}
          sx={{ pointerEvents: reading_ ? 'none' : 'auto' }}
        >
          {/* Not while anything they sent is still being read: the way on
              would end the turn with something half read. They come back
              once the reading is done. */}
          {question.quickReplies &&
            !editingCard &&
            draft === undefined &&
            !listening &&
            !transcript.some((m) => m.reading) && (
              <QuickReplies
                replies={question.quickReplies}
                onPick={(reply) =>
                  reply.go ? moveOn(reply.label) : answer(reply.label)
                }
              />
            )}
          {heard && captionsOn && (draft !== undefined || listening) && (
            <LiveCaptionCard words={heard} listening={listening} />
          )}
          {editingCard && (
            // The card's lines go into the field itself, to change and send.
            // This line says the field is holding a correction, and closes it.
            <Stack
              direction='row'
              sx={{ alignItems: 'center', justifyContent: 'space-between' }}
            >
              <Stack
                direction='row'
                spacing={0.75}
                sx={{ alignItems: 'center', color: 'primary.main' }}
              >
                <Icon icon={'pencil' as never} fontSize={12} />
                <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                  Editing
                </Typography>
              </Stack>
              <IconButton
                aria-label='Stop editing'
                size='small'
                onClick={() => {
                  setEditingCard(null);
                  setEditText('');
                }}
              >
                <Icon icon={'xmark' as never} fontSize={12} />
              </IconButton>
            </Stack>
          )}
          {draft !== undefined || listening ? (
            // Recording and the recording waiting to be sent are one row, so
            // stopping moves things rather than swapping the bar: the bin
            // slides in from the left as the note narrows to make room, and
            // stop turns into send in the same place.
            //
            // Heard back before it goes, so sending is a decision rather than
            // a side effect of stopping.
            <Stack sx={{ alignItems: 'center' }} direction='row'>
              <Box
                sx={{
                  display: 'flex',
                  flexShrink: 0,
                  overflow: 'hidden',
                  width: draft !== undefined ? 40 : 0,
                  mr: draft !== undefined ? 1 : 0,
                  opacity: draft !== undefined ? 1 : 0,
                  transition: `width ${REC_MS}ms ${REC_EASE}, margin ${REC_MS}ms ${REC_EASE}, opacity ${REC_MS}ms ${REC_EASE}`,
                }}
              >
                <IconButton
                  aria-label='Delete recording'
                  tabIndex={draft !== undefined ? 0 : -1}
                  onClick={() => setDraft(undefined)}
                  // Red, because it throws the recording away. Beside the send
                  // button it read as one of a pair of neutral controls, which
                  // is the wrong thing to be neutral about.
                  sx={{ color: 'error.main' }}
                >
                  <Icon icon={'trash' as never} fontSize={16} />
                </IconButton>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <VoiceNote
                  seconds={draft ?? elapsed}
                  tone={draft !== undefined ? 'draft' : 'recording'}
                />
              </Box>
              {/* A quiet switch, in the row rather than over it, so nothing
                  jumps in as recording starts. Captions are made either way;
                  this only shows them. */}
              {liveCaptions && (
                <IconButton
                  aria-label={
                    captionsOn ? 'Hide live captions' : 'Show live captions'
                  }
                  aria-pressed={captionsOn}
                  onClick={() => setCaptionsOn((on) => !on)}
                  sx={{
                    ml: 1,
                    // The send button's size, so the two sit level.
                    width: sendButton.width,
                    height: sendButton.height,
                    flexShrink: 0,
                    color: captionsOn ? 'primary.main' : 'text.disabled',
                    bgcolor: captionsOn ? 'action.selected' : 'transparent',
                  }}
                >
                  <Icon icon={'closed-captioning' as never} fontSize={18} />
                </IconButton>
              )}
              <IconButton
                aria-label={
                  draft !== undefined ? 'Send recording' : 'Stop recording'
                }
                onClick={() => {
                  if (draft !== undefined) {
                    send(draft);
                    return;
                  }
                  // Stopping stops the clock as well, so throwing the note away
                  // goes back to an empty field rather than to recording.
                  setDraft(elapsed);
                  setListening(false);
                }}
                sx={{ ...sendButton, ml: 1 }}
              >
                {/* Keyed so the icon fades in as it changes. */}
                <Box
                  key={draft !== undefined ? 'send' : 'stop'}
                  sx={{
                    display: 'flex',
                    animation: `recIconIn ${REC_MS}ms ${REC_EASE}`,
                    '@keyframes recIconIn': {
                      from: { opacity: 0, transform: 'scale(0.6)' },
                      to: { opacity: 1, transform: 'scale(1)' },
                    },
                  }}
                >
                  <Icon
                    icon={
                      (draft !== undefined
                        ? 'paper-plane-top'
                        : 'square') as never
                    }
                    fontSize={draft !== undefined ? 16 : 14}
                  />
                </Box>
              </IconButton>
            </Stack>
          ) : liveVoice && !editingCard ? (
            // A card rather than a pill, the way Claude's composer is: what
            // they type on top, with room, and the controls in a row under
            // it. How to use sits where Claude has the model, for anyone
            // unsure they can just talk or type.
            //
            // Talking happens in the same card, so nothing jumps: the mic
            // stretches left into a pill holding the wave, with stop where
            // the mic was, and their words take the line they would type on.
            <Stack
              spacing={1}
              sx={{
                p: 1,
                pt: 1.5,
                borderRadius: `${radius.lg}px`,
                border: 1,
                borderColor: 'custom.neutral.200',
                bgcolor: 'background.paper',
                opacity: reading_ && !talking ? 0.4 : 1,
                transition: 'opacity .2s ease',
              }}
            >
              {talking ? (
                <Typography
                  variant='body1'
                  aria-live='polite'
                  noWrap
                  sx={{
                    px: 1,
                    minHeight: 32,
                    display: 'flex',
                    alignItems: 'center',
                    fontStyle: 'italic',
                    color:
                      hearing && liveLine ? 'text.secondary' : 'text.disabled',
                  }}
                >
                  {hearing ? liveLine || 'Listening' : ''}
                </Typography>
              ) : (
                <InputBase
                  value={text}
                  placeholder={question.placeholder ?? 'Type your answer'}
                  multiline
                  maxRows={5}
                  disabled={done || reading_}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter' || e.shiftKey) return;
                    e.preventDefault();
                    if (stagedPhoto) sendStaged();
                    else if (text.trim()) answer(text.trim());
                  }}
                  sx={{ px: 1, minHeight: 32 }}
                />
              )}
              <Box sx={{ position: 'relative', height: TAP }}>
                {/* Put away under the pill while talking. */}
                <Stack
                  direction='row'
                  spacing={1}
                  sx={{
                    alignItems: 'center',
                    opacity: talking ? 0 : 1,
                    pointerEvents: talking ? 'none' : 'auto',
                    transition: `opacity ${LIVE_GROW_MS / 2}ms ease`,
                  }}
                >
                  <IconButton
                    aria-label='Add a photo or a file'
                    disabled={reading_}
                    onClick={() => setAttaching(true)}
                    sx={pillButton}
                  >
                    <Icon icon={'plus' as never} fontSize={16} />
                  </IconButton>
                  <Chip
                    label='How to use'
                    onClick={() => setHowTo(true)}
                    // The circles' height, so the row reads as one line of
                    // controls.
                    sx={{
                      height: pillButton.height,
                      borderRadius: pillButton.height / 2,
                    }}
                  />
                </Stack>
                {/* The wave grows left out of the mic, on the card itself.
                    The button stays the coral circle it was, at the right
                    end, so only the button is coloured. */}
                <Stack
                  direction='row'
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    height: TAP,
                    width: talking ? '100%' : TAP,
                    borderRadius: TAP / 2,
                    alignItems: 'center',
                    overflow: 'hidden',
                    transition: `width ${LIVE_GROW_MS}ms ${REC_EASE}`,
                  }}
                >
                  {/* A soundwave of them talking. Flat on our turn, so it
                      only moves when it is hearing them. In after the pill
                      has grown, out before it shrinks. */}
                  <Stack
                    role='status'
                    aria-label={talking ? liveState : undefined}
                    direction='row'
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      height: 20,
                      // Only while open: closed, the padding alone pushed the
                      // mic off centre.
                      pl: talking ? 1.5 : 0,
                      pr: talking ? 1.5 : 0,
                      gap: '2px',
                      alignItems: 'center',
                      // Spread across the whole space left of stop.
                      justifyContent: 'space-between',
                      overflow: 'hidden',
                      opacity: talking ? 1 : 0,
                      transition: talking
                        ? `opacity 200ms ease ${LIVE_GROW_MS * 0.6}ms`
                        : 'opacity 120ms ease',
                    }}
                  >
                    {LIVE_PEAKS.map((peak, i) => (
                      <Box
                        key={i}
                        sx={{
                          width: 3,
                          height: '100%',
                          flexShrink: 0,
                          borderRadius: '2px',
                          bgcolor: 'text.primary',
                          opacity: hearing ? 1 : 0.5,
                          '--peak': peak,
                          transform: 'scaleY(0.12)',
                          transition: 'transform .3s ease, opacity .3s ease',
                          // One wave travelling along the bars: the same rise
                          // and fall on each, a beat behind the one before.
                          animation:
                            talking && hearing
                              ? `liveWave 1.3s ease-in-out ${-i * 0.09}s infinite`
                              : 'none',
                          '@keyframes liveWave': {
                            '0%, 100%': { transform: 'scaleY(0.12)' },
                            '50%': { transform: 'scaleY(var(--peak))' },
                          },
                          '@media (prefers-reduced-motion: reduce)': {
                            animation: 'none',
                            transform:
                              talking && hearing
                                ? 'scaleY(var(--peak))'
                                : 'scaleY(0.12)',
                          },
                        }}
                      />
                    ))}
                  </Stack>
                  {/* Where the mic was: stop while talking, which puts what
                      they said in the box to fix before sending, rather than
                      it sending on the pause. Send once they type. */}
                  <IconButton
                    aria-label={
                      talking
                        ? 'Stop'
                        : micInFieldFor(question)
                          ? 'Talk'
                          : 'Send'
                    }
                    disabled={!talking && (done || reading_)}
                    onClick={() => {
                      if (talking) {
                        setTalking(false);
                        // The whole answer, standing in for what they had
                        // said by the time they tapped, so a quick tap still
                        // shows something to edit.
                        if (!speaking && q?.answer) {
                          setText((t) =>
                            [t.trim(), q.answer].filter(Boolean).join(' '),
                          );
                        }
                      } else if (micInFieldFor(question)) {
                        setTalking(true);
                        setPlaying(true);
                      } else if (stagedPhoto) sendStaged();
                      else answer(text.trim());
                    }}
                    sx={tapButton}
                  >
                    <Icon
                      icon={
                        (talking
                          ? 'stop'
                          : micInFieldFor(question)
                            ? 'microphone'
                            : 'arrow-up') as never
                      }
                      type={talking ? 'fas' : undefined}
                      fontSize={talking ? 14 : 16}
                    />
                  </IconButton>
                </Stack>
              </Box>
            </Stack>
          ) : (
            // To the foot, so the buttons stay by the last line as the field
            // grows.
            <Stack sx={{ alignItems: 'flex-end' }} direction='row' spacing={1}>
              <TextField
                size='small'
                variant='outlined'
                // Correcting a card, the field holds its lines.
                ref={fieldRef}
                value={editingCard ? editText : text}
                placeholder={question.placeholder}
                // Grows with what is typed, up to five lines, then scrolls. A
                // card's lines get half the screen, so a whole summary fits.
                multiline
                maxRows={editingCard ? undefined : 5}
                inputRef={editingCard ? editRef : undefined}
                disabled={done || reading_}
                onChange={(e) =>
                  editingCard
                    ? setEditText(e.target.value)
                    : setText(e.target.value)
                }
                onKeyDown={(e) => {
                  // Enter still sends; shift and Enter is a new line.
                  if (e.key !== 'Enter' || e.shiftKey) return;
                  e.preventDefault();
                  if (editingCard) {
                    if (editText.trim()) answer(editText.trim());
                  } else if (stagedPhoto) sendStaged();
                  else if (text.trim()) answer(text.trim());
                }}
                // The camera lives in the field: taking a photo is a way of
                // answering, not a control sitting beside the answer.
                slotProps={{
                  input: {
                    startAdornment: editingCard ? undefined : (
                      <InputAdornment position='start'>
                        <IconButton
                          aria-label='Add a photo or a file'
                          size='small'
                          disabled={reading_}
                          onClick={() => setAttaching(true)}
                        >
                          <Icon icon={'plus' as never} fontSize={18} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  opacity: reading_ ? 0.4 : 1,
                  transition: 'opacity .2s ease',
                  // A pill on one line. Several lines tall, holding a card's
                  // lines or a long answer typed, a pill goes lozenge, so it
                  // takes the captions card's corners instead.
                  '& .MuiOutlinedInput-root': {
                    borderRadius: `${editingCard || wrapped ? radius.md : radius.xxl}px`,
                    pl: 1,
                  },
                  // Past half the screen a correction scrolls inside; the
                  // autosize sets overflow hidden inline, hence the important.
                  ...(editingCard && editMax
                    ? {
                        '& textarea': {
                          maxHeight: `${editMax}px`,
                          overflowY: 'auto !important',
                        },
                      }
                    : {}),
                }}
              />
              {micInFieldFor(question) && !liveVoice ? (
                // Nothing typed yet: the button records instead of sending.
                <IconButton
                  aria-label='Record a voice note'
                  disabled={done || reading_}
                  onClick={() => {
                    setElapsed(0);
                    setListening(true);
                  }}
                  sx={sendButton}
                >
                  <Icon icon={'microphone' as never} fontSize={16} />
                </IconButton>
              ) : (
                <IconButton
                  aria-label='Send'
                  disabled={
                    done ||
                    reading_ ||
                    (Boolean(editingCard) && !editText.trim())
                  }
                  onClick={() =>
                    editingCard
                      ? answer(editText.trim())
                      : stagedPhoto
                        ? sendStaged()
                        : answer(text.trim())
                  }
                  sx={sendButton}
                >
                  <Icon icon={'paper-plane-top' as never} fontSize={16} />
                </IconButton>
              )}
            </Stack>
          )}
        </Stack>
      </Box>
    );

  // The ask button floats over the page while the empty state is up, in place
  // of the composer, until it is tapped.
  const askFloating =
    Boolean(askButton) &&
    !asking &&
    Boolean(emptyState) &&
    transcript.length === 0;

  return (
    <ScreenRoot>
      {!desktop && <StatusBar />}
      {bar}
      {account && (
        <AccountBar
          {...account}
          // Which set of questions you are in, and it changes as they do. A
          // fixed journey name said the same thing as the opening section's
          // caption, so the two stacked and said it twice; falls back to the
          // journey on a screen that has no sections.
          label={q?.section ?? account.label}
          // The way out goes in the bar beside the avatar. Under it, on the
          // caption strip, it was an icon alone on a rule: the strip's other
          // half is the section, and the section moved up here.
          action={
            onClose && (
              <IconButton aria-label='Close' size='small' onClick={onClose}>
                <Icon icon={'xmark' as never} fontSize={16} />
              </IconButton>
            )
          }
          initials={
            namedFrom && index <= questions.findIndex((q) => q.id === namedFrom)
              ? undefined
              : account.initials
          }
        />
      )}

      {/* Whose questions these are, and a way out where there is one. Opened
          from the start of the intake there is nowhere to close to; reopened
          over the summary, there is.

          The section is the bar's title wherever there is an account bar, so
          this strip is down to the way out. The calculator's chat has its own
          bar and no account, so it still captions the section here. Nothing to
          say and nowhere to close to renders no strip at all, rather than an
          empty one with a second hairline under the bar's. */}
      {section && (
        <>
          <Stack
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
            }}
            direction='row'
          >
            <Typography
              sx={{ py: 1.5, color: 'text.secondary' }}
              variant='caption'
            >
              {section}
            </Typography>
            {!account && onClose && (
              <IconButton aria-label='Close' size='small' onClick={onClose}>
                <Icon icon={'xmark' as never} fontSize={16} />
              </IconButton>
            )}
          </Stack>
          <Divider />
        </>
      )}

      {/* The scrim over everything above the chat: the status bar and the app
          bar. Anchored to the chat's top edge and reaching up, so it meets the
          card's scrim there with no seam, and the screen's own clip stops it. */}
      <Box sx={{ position: 'relative', height: 0 }}>
        <Box
          sx={(theme) => ({
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '100vh',
            zIndex: 2,
            pointerEvents: 'none',
            bgcolor: alpha(theme.palette.text.primary, PIN_SCRIM),
            opacity: dimmed ? 1 : 0,
            transition: `opacity ${PIN_SCRIM_MS}ms ease-in-out`,
          })}
        />
      </Box>
      <Box
        ref={scrollerRef}
        sx={{
          flex: 1,
          overflow: 'auto',
          position: 'relative',
          px: 3,
          pt: 2,
          pb: 2,
        }}
      >
        {/* An empty chat opens on its greeting, in the middle, until anything
            is said. */}
        {emptyState && transcript.length === 0 && emptyState}
        {greeting && transcript.length === 0 && (
          <Stack
            sx={{
              height: '100%',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              px: 2,
            }}
            spacing={2}
          >
            <Box
              sx={{ width: 40, height: 'auto' }}
              component='img'
              src='/vector/karehero-logo.png'
              alt='KareHero'
            />
            <Typography variant='h2'>{greeting}</Typography>
          </Stack>
        )}
        <Stack ref={stackRef} spacing={1.5}>
          {intro && (
            // A row over Keni's first message, the way a chat names who is
            // talking: Keni in a circle, then the line, left aligned with the
            // messages so it belongs to them rather than floating above.
            <Stack
              sx={{ alignItems: 'center', pt: 1 }}
              direction='row'
              spacing={1.5}
            >
              <ButtonBase
                aria-label='About Keni'
                onClick={() => setAboutKeni(true)}
                sx={{ borderRadius: '50%', flexShrink: 0 }}
              >
                <KeniAvatar size={56} />
              </ButtonBase>
              {/* Charcoal, set here: the subtitle variant brings a lighter
                  colour of its own, which is too faint on the page. */}
              <Typography sx={{ color: 'text.primary' }} variant='subtitle1'>
                {intro.title}
              </Typography>
            </Stack>
          )}
          {transcript.map((m, i) =>
            withReading(
              m,
              i,
              m.menuFor ? (
                <ChatMenuMessage
                  key={i}
                  title={
                    questions.find((x) => x.id === m.menuFor)?.attachRows?.title
                  }
                  text={m.text}
                  options={
                    questions.find((x) => x.id === m.menuFor)?.asides ?? []
                  }
                  more={
                    questions.find((x) => x.id === m.menuFor)?.attachRows?.more
                  }
                  onPick={q?.id === m.menuFor ? answer : undefined}
                  keep={questions
                    .find((x) => x.id === m.menuFor)
                    ?.explain?.filter((e) => e.keep)
                    .map((e) => e.label)}
                  // By the question the menu belongs to, not the one on
                  // screen, so a kept row still opens once it is answered.
                  onMore={(label) => {
                    const owner = questions.find((x) => x.id === m.menuFor);
                    if (!owner?.explain?.some((e) => e.label === label)) {
                      return false;
                    }
                    setExplaining(label);
                    return true;
                  }}
                  // Whatever they said next is the answer to it.
                  picked={
                    (q?.id === m.menuFor ? demoPicked : undefined) ??
                    transcript
                      .slice(i + 1)
                      .find((x) => x.from === 'user' && !x.aside)?.text
                  }
                  defaultOpen={optionsOpen && q?.id === m.menuFor}
                  many={questions.find((x) => x.id === m.menuFor)?.many}
                  ticked={
                    q?.id === m.menuFor
                      ? questions.find((x) => x.id === m.menuFor)?.ticked
                      : undefined
                  }
                  {...menuChange(m.menuFor)}
                />
              ) : m.claims ? (
                <Stack key={i} spacing={2}>
                  {m.claims.map((claim) => (
                    <ClaimRow key={claim.name} {...claim} />
                  ))}
                </Stack>
              ) : m.facts ? (
                <FactList key={i} groups={m.facts} />
              ) : m.card ? (
                m.card.checklistUntil ? (
                  <ChecklistCard
                    key={i}
                    card={m.card}
                    questions={questions}
                    index={index}
                    transcript={transcript}
                    reading={reading_}
                    recording={listening}
                    composing={
                      listening || draft !== undefined || Boolean(text.trim())
                    }
                    missed={missed}
                    onDim={setDimmed}
                  />
                ) : (
                  <FromUsCard
                    key={i}
                    {...m.card}
                    // Editable only while it is the thing being asked about. By
                    // label, not identity: an edit replaces the card object.
                    // Only the latest of it, once it has been corrected.
                    onEdit={
                      m.card.editable &&
                      m.card.label === q?.card?.label &&
                      transcript.findLastIndex(
                        (x) => x.card?.label === m.card?.label,
                      ) === i
                        ? m.card.facts
                          ? () => setFactsCard(m.card!)
                          : () => {
                              setEditingCard({ at: i, label: m.card!.label });
                              setEditText((m.card!.items ?? []).join('\n'));
                            }
                        : undefined
                    }
                  >
                    {/* The way on, as the Home card has it. */}
                    {m.card.action && (
                      <Button
                        variant='primary'
                        isFullWidth
                        ariaLabel={m.card.action}
                        onPress={() => onComplete?.(answers.current)}
                      >
                        {m.card.action}
                      </Button>
                    )}
                  </FromUsCard>
                )
              ) : m.failure ? (
                <FailureRow key={i} kind={m.failure} />
              ) : m.aside ? (
                <AsideRow
                  key={i}
                  aside={m.aside}
                  selectable={isChoice(q, m.aside)}
                  selected={chosen.includes(m.aside.label)}
                  onClick={
                    m.aside.tone === 'go'
                      ? moveOn
                      : m.aside.opensInput
                        ? () => openInput(q.id)
                        : m.aside.uploads
                          ? () => upload(m.aside!.uploads!, m.aside!.file)
                          : () => answer(m.aside!.label)
                  }
                />
              ) : m.attachment ? (
                m.attachmentState === 'failed' ? (
                  <FailedMessage
                    key={i}
                    label='Tap to try again'
                    onResend={retryUpload}
                  >
                    <Attachment
                      kind={m.attachment}
                      name={m.text}
                      state='failed'
                    />
                  </FailedMessage>
                ) : (
                  <Box key={i} sx={{ alignSelf: 'flex-end', maxWidth: '82%' }}>
                    <Attachment
                      kind={m.attachment}
                      name={m.text}
                      state={m.attachmentState}
                    />
                  </Box>
                )
              ) : m.seconds ? (
                m.failed ? (
                  <FailedMessage key={i} onResend={resend}>
                    <VoiceNote seconds={m.seconds} tone='failed' />
                  </FailedMessage>
                ) : (
                  <Box key={i} sx={{ alignSelf: 'flex-end' }}>
                    <VoiceNote
                      seconds={m.seconds}
                      transcript={m.transcript}
                      // One setting with the captions: seeing what they
                      // said, on every note and while recording, whichever
                      // way they last switched it.
                      transcriptShown={captionsOn}
                      onToggleTranscript={() => setCaptionsOn((on) => !on)}
                    />
                  </Box>
                )
              ) : m.failed ? (
                <FailedMessage key={i} onResend={resend}>
                  <ChatBubble from={m.from} text={m.text} failed />
                </FailedMessage>
              ) : m.qi !== undefined ? (
                // A reply that can be changed: tapping it opens the question's
                // rows in a sheet.
                <ChatBubble
                  key={i}
                  from={m.from}
                  text={m.text}
                  onClick={() =>
                    // A menu's reply opens the menu again; the rest a sheet.
                    questions[m.qi!]?.attachRows
                      ? setReopenedMenu(m.qi!)
                      : setChanging({
                          qi: m.qi!,
                          picked: transcript
                            .filter((r) => r.qi === m.qi && r.from === 'user')
                            .map((r) => r.text),
                        })
                  }
                />
              ) : (
                <ChatBubble key={i} from={m.from} text={m.text} />
              ),
              i === lastSent,
            ),
          )}

          {/* The orb straddles the bar below and pokes 40px up into the
              transcript, so the last row needs room under it. A spacer rather
              than padding on the scroll box: the transcript scrolls its last
              child to the bottom edge, which scrolls padding out of sight and
              leaves the way on sitting under the orb. */}
          {/* Above the orb's spacer, so the dots sit with the messages rather
              than below the room kept for the orb. */}
          {thinking &&
            (readingNow ? (
              <ReadingBubble lines={readingNow.lines} />
            ) : (
              <ChatTyping />
            ))}

          {footerKind(shownFooter, shownUnlocked) === 'spoken' && (
            <Box sx={{ height: '48px', flexShrink: 0 }} />
          )}
        </Stack>
      </Box>

      {/* The bar that is going drops away, then the one arriving comes up. A
          closed question and an open one need different controls, and swapping
          them in place read as a flicker: this way the change is legible as a
          change. */}
      <Box
        sx={{
          flexShrink: 0,
          transform: footerIn ? 'none' : 'translateY(100%)',
          transition: 'transform .22s ease',
        }}
        onTransitionEnd={settleFooter}
      >
        {askFloating && askButton ? (
          // Floating over the page rather than a bar under it: a footer with
          // no height, and the button hung above its top edge.
          <Box sx={{ position: 'relative', height: 0 }}>
            <FloatingAsk
              label={askButton.label}
              tone={askButton.tone}
              onPress={() => setAsking(true)}
            />
          </Box>
        ) : shownUnlocked ? (
          // Mounted the moment the composer is asked for, which is what makes
          // the keyframe run: this element did not exist a frame ago. Same way
          // the drawers and the provider overlay arrive.
          <Box
            sx={{
              '@keyframes composerIn': {
                from: { transform: 'translateY(100%)' },
                to: { transform: 'translateY(0)' },
              },
              animation: 'composerIn .28s cubic-bezier(0.22, 1, 0.36, 1)',
            }}
          >
            {footerBar(shownFooter)}
          </Box>
        ) : (
          footerBar(shownFooter)
        )}
      </Box>
      {keyboard && (
        <Box sx={{ position: 'relative', height: KEYBOARD_H, flexShrink: 0 }}>
          <MobileKeyboard visible />
        </Box>
      )}
      {/* The safe area belongs to the bar above it, so it takes its colour, and
          it follows the bar you can see rather than the live question: it
          changes as the bar arrives instead of ahead of it. A question answered
          in the conversation has no bar, so there is nothing for the strip to be
          the bottom of and it is just the page. */}
      <Box
        sx={{
          flexShrink: 0,
          // A floating button has no bar to be the bottom of either.
          bgcolor:
            askFloating || footerKind(shownFooter, shownUnlocked) === 'none'
              ? 'background.default'
              : 'background.paper',
        }}
      >
        {!desktop && !keyboard && <HomeIndicator />}

        {factsCard?.facts && (
          <FactsSheet
            title={factsCard.label}
            groups={factsCard.facts}
            onClose={() => setFactsCard(null)}
          />
        )}

        {/* Titled, and built from the rows the help sheet on the funding screen
          uses, so the two sheets in this flow are the same sheet. */}
        {viewingStaged && (
          <ModalScreen onClose={() => setViewingStaged(false)}>
            {(close) => (
              <Box
                sx={{
                  position: 'relative',
                  height: '100%',
                  bgcolor: 'custom.charcoal',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    aspectRatio: '3 / 4',
                    backgroundImage: 'url(/vector/photo-placeholder.svg)',
                    backgroundSize: 'cover',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'custom.neutral.500',
                  }}
                >
                  <Icon icon={'id-card' as never} fontSize={64} />
                </Box>
                <IconButton
                  aria-label='Close'
                  onClick={close}
                  sx={{
                    position: 'absolute',
                    top: 56,
                    right: 16,
                    color: 'common.white',
                  }}
                >
                  <Icon icon={'xmark' as never} fontSize={20} />
                </IconButton>
              </Box>
            )}
          </ModalScreen>
        )}

        {changing &&
          (() => {
            const question = questions[changing.qi];
            const rows = (question?.asides ?? []).filter(isAnswerRow);
            const multi = Boolean(question?.multi);
            return (
              <BottomSheet
                title={question?.ask}
                variant='hug'
                onClose={() => setChanging(null)}
                footer={
                  multi
                    ? (close) => (
                        <Button
                          variant='primary'
                          size='lg'
                          isFullWidth
                          ariaLabel='Done'
                          onPress={() => {
                            applyChange(changing.qi, changing.picked);
                            close();
                          }}
                        >
                          Done
                        </Button>
                      )
                    : undefined
                }
              >
                {(close) => (
                  // The question's own rows, with the answer on the record
                  // ticked. A pick-one changes on the tap; a pick-several
                  // toggles and changes on Done.
                  <Stack sx={{ pb: 2 }} spacing={1.5}>
                    {rows.map((row) => {
                      const on = changing.picked.includes(row.label);
                      return (
                        <ChatActionRow
                          key={row.label}
                          label={row.label}
                          icon={on ? 'check' : undefined}
                          tone={on ? 'go' : 'offer'}
                          onClick={() => {
                            if (multi) {
                              setChanging({
                                ...changing,
                                picked: on
                                  ? changing.picked.filter(
                                      (v) => v !== row.label,
                                    )
                                  : [...changing.picked, row.label],
                              });
                              return;
                            }
                            applyChange(changing.qi, [row.label]);
                            close();
                          }}
                        />
                      );
                    })}
                  </Stack>
                )}
              </BottomSheet>
            );
          })()}

        {aboutKeni && intro?.about && (
          <BottomSheet
            title={intro.about.title}
            variant='hug'
            onClose={() => setAboutKeni(false)}
          >
            <Stack sx={{ pb: 3 }} spacing={2}>
              {/* Keni, big, so the drawer about Keni shows who it is about. */}
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <KeniAvatar size={128} />
              </Box>
              {intro.about.points.map((point) => (
                <Typography
                  key={point}
                  sx={{ color: 'text.primary' }}
                  variant='body1'
                >
                  {point}
                </Typography>
              ))}
            </Stack>
          </BottomSheet>
        )}

        {explained && (
          <BottomSheet
            title={explained.title}
            variant='hug'
            onClose={() => setExplaining(undefined)}
          >
            <Stack sx={{ pb: 3 }} spacing={2}>
              {explained.points.map((point) => (
                <Typography
                  key={point}
                  sx={{ color: 'text.primary' }}
                  variant='body1'
                >
                  {point}
                </Typography>
              ))}
              {explained.link && (
                <Typography sx={{ color: 'text.primary' }} variant='body1'>
                  {explained.link.lead}{' '}
                  {explained.link.href ? (
                    <Link
                      href={explained.link.href}
                      target='_blank'
                      rel='noreferrer'
                    >
                      {explained.link.label}
                    </Link>
                  ) : (
                    <Link
                      component='button'
                      type='button'
                      onClick={onExplainLink}
                    >
                      {explained.link.label}
                    </Link>
                  )}
                </Typography>
              )}
            </Stack>
          </BottomSheet>
        )}

        {attaching && (
          <BottomSheet
            title={INTAKE_ATTACH.title}
            variant='hug'
            onClose={() => setAttaching(false)}
          >
            {/* The rows the help sheet on the funding screen uses: an option
                Card each, close together. Two of them, so a stock List's own
                spacing read as a settings screen rather than a choice. */}
            <Stack sx={{ pb: 2 }} spacing={1}>
              {(
                [
                  ['photo', INTAKE_ATTACH.photos, 'camera'],
                  ['document', INTAKE_ATTACH.files, 'file-lines'],
                ] as const
              ).map(([kind, label, icon]) => (
                <Card
                  key={kind}
                  variant='option'
                  onClick={() => {
                    setAttaching(false);
                    // A photo waits in the composer to be looked at first. A
                    // file has nothing to preview, so it goes straight away.
                    if (kind === 'photo') setStagedPhoto(true);
                    else upload(kind);
                  }}
                >
                  <Stack
                    sx={{ alignItems: 'center' }}
                    direction='row'
                    spacing={1.5}
                  >
                    <Icon icon={icon as never} fontSize={18} />
                    <Typography variant='h6'>{label}</Typography>
                  </Stack>
                </Card>
              ))}
            </Stack>
          </BottomSheet>
        )}

        {howTo && (
          <BottomSheet
            title={INTAKE_HOW_TO.title}
            variant='hug'
            onClose={() => setHowTo(false)}
          >
            <Stack sx={{ pb: 2 }} spacing={2}>
              <Typography variant='body1'>{INTAKE_HOW_TO.body}</Typography>
              {INTAKE_HOW_TO.rows.map(([icon, line]) => (
                <Stack
                  key={icon}
                  direction='row'
                  spacing={1.5}
                  sx={{ alignItems: 'center' }}
                >
                  <Box
                    sx={{
                      ...pillButton,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                    }}
                  >
                    <Icon icon={icon as never} fontSize={16} />
                  </Box>
                  <Typography variant='body1'>{line}</Typography>
                </Stack>
              ))}
            </Stack>
          </BottomSheet>
        )}
      </Box>
    </ScreenRoot>
  );
};

export default ChatV2;
